/**
 * Enemy tanks: per-layer spawning (at random spots across the arena, staggered,
 * rising up in place) and the AI — drive + wall-avoid + wander, aim the turret at
 * the player (with per-type error and lead), fire when they have a clear line and
 * a free shell slot, and lay mines. Each enemy is one of your Bluesky mutuals,
 * coloured by that mutual's identity.
 */

import * as THREE from "three";
import { TANK, BULLET, MINE, SIM_SPEED, MECHA, mechaEligible, mechaPlan, enemiesForLayer, enemyFieldLimit, monoRosterForLayer, TANK_TYPES, type TankType } from "../config";
import { WALLS, INTERIOR, resolveCircle, slideCircle, clampInterior, reflectCircle, isOverSpawnHole, isOverElevator, dynWallsAlong } from "../world/gamemap";
import { Tank, yawToward, approachAngle } from "../entities/tank";
import { sfx } from "../engine/sfx";
import type { Bullets } from "./bullets";
import { ARC_BLAST } from "./bullets";
import { type Treads, TREAD_STEP2 } from "./treads";
import { Lasers } from "./lasers";
import type { Effects } from "./effects";
import type { UpgradeLevers } from "./upgrades";
import type { loadTankGeometries } from "../tank/prefab";
import type { Arena, FlowStep } from "../world/arena";

const SNIPER_LIFE = 8; // long-lived sniper shells so multi-bounce paths complete
const ZERO_VEC = new THREE.Vector3(); // no-lead velocity for non-player targets (mercenaries)

/** Sticky wall-follow bookkeeping for a forward-only mover. Call AFTER resolveCircle with
 *  the resolve push-out (pushX,pushZ = the contact normal), how far the tank actually moved
 *  this frame (moved2) vs. what it intended (goal2), and the goal heading (gx,gz). When the
 *  tank is pinned (real push-out + little progress) it commits, on first contact, to ONE way
 *  around — the tangent nearer the goal — and holds it until clear. The steering stage then
 *  glides along blockNX/NZ·blockSide. This is what frees forward-only tanks from wedge
 *  diagonals, where plain slide (zero tangent on a dead-on hit) and the alternating juke
 *  (jitters) both fail; verified empirically (pin frames 462→~90, 0%→~85% cleared). */
function noteBlock(t: Tank, dt: number, pushX: number, pushZ: number, moved2: number, goal2: number, gx: number, gz: number): void {
  const pl = Math.hypot(pushX, pushZ);
  if (pl > 0.2 && moved2 < goal2) {
    if (t.blockT <= 0) { const tx = -pushZ / pl, tz = pushX / pl; t.blockSide = tx * gx + tz * gz >= 0 ? 1 : -1; }
    t.blockNX = pushX / pl; t.blockNZ = pushZ / pl; t.blockT = 0.35;
  } else t.blockT -= dt;
}
/** Billiard-unfolded image of coordinate p reflected n times in [lo,hi]. */
function imageCoord(n: number, p: number, lo: number, hi: number): number {
  if (n === 0) return p;
  const W = hi - lo, m = Math.abs(n);
  if (n > 0) return m % 2 === 1 ? 2 * hi - p + (m - 1) * W : p + m * W;
  return m % 2 === 1 ? 2 * lo - p - (m - 1) * W : p - m * W;
}

/** The mirror-image (i,j) candidates ordered by bounce count (1..5). One entry =
 *  one bank-shot candidate; the sniper tests these incrementally across frames. */
const BANK_IMAGES: Array<readonly [number, number]> = (() => {
  const out: Array<readonly [number, number]> = [];
  for (let b = 1; b <= 5; b++) for (let i = -b; i <= b; i++) {
    const jMag = b - Math.abs(i);
    const jList = jMag === 0 ? [0] : [jMag, -jMag];
    for (const js of jList) { if (i === 0 && js === 0) continue; out.push([i, js]); }
  }
  return out;
})();

// reused scratch for the forward-sim (no per-call allocation → no GC hitches)
const _simPos = { x: 0, z: 0 };
const _simVel = { x: 0, z: 0 };
// scratch for the merc trajectory-prediction forward-sim
const _trajP = { x: 0, z: 0 };
const _trajV = { x: 0, z: 0 };

/** Forward-simulate a shot along (dx,dz) — true if it reaches the player within
 *  `maxBounces` ricochets and `range` world units. Coarse step → cheap (~0.2ms).
 *  Snipers use the defaults (5 bounces, a full shell's reach); the Laser passes a
 *  tighter cap so its solved bank matches its (short, 2-bounce) beam. */
function validateBankDir(sx: number, sz: number, dx: number, dz: number, px: number, pz: number, speed: number, maxBounces = 5, range = speed * SNIPER_LIFE): boolean {
  const pos = _simPos, vel = _simVel;
  pos.x = sx; pos.z = sz; // start AT the muzzle — the precise shell does too, so its 5-unit step grid lines up exactly (a +4 skip here desynced the bounce path → misses)
  vel.x = dx * speed; vel.z = dz * speed;
  const step = 5, sdt = step / speed;
  let bounces = 0, travel = 0;
  while (travel < range) {
    pos.x += vel.x * sdt; pos.z += vel.z * sdt;
    if (reflectCircle(pos, vel, BULLET.radius, true)) { bounces++; if (bounces > maxBounces) return false; }
    const ex = px - pos.x, ez = pz - pos.z;
    if (ex * ex + ez * ez <= 12 * 12) return true;
    travel += step;
  }
  return false;
}

// laser charge/beam cycle + bounce-beam tuning
const LASER_CHARGE = 3;   // seconds the beam winds up (red, fading in, telegraphing)
const LASER_BEAM = 3;     // seconds the beam is live (pink, burning)
const LASER_LOCK = 0.5;   // freeze the aim this long before the beam goes live (so the line is dodgeable)
const LASER_BOUNCES = 2;  // ricochets the beam (and its aim solver) uses
const LASER_RANGE = 900; // beam length budget — matches traceLaser's RANGE so a solved bank is one the beam actually reaches
const LASER_RED = 0xff0000, LASER_PINK = 0xff3df0, LASER_CORE = 0xffd2ff;

/** Linear blend between two 0xRRGGBB colours. */
function lerpHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  return ((Math.round(ar + (br - ar) * t) << 16) | (Math.round(ag + (bg - ag) * t) << 8) | Math.round(ab + (bb - ab) * t));
}

const FLOW_OUT: FlowStep = { kind: "none", dx: 0, dz: 0, toLevel: 0 };
const RIDE_CELL = { x: 0, z: 0 };

type Geo = Awaited<ReturnType<typeof loadTankGeometries>>;

/** True if a wall/obstacle (except `skip`) blocks the segment a→b (slab test).
 *  Ground solids only block level-0 sight lines; road shells are above them. */
function segmentBlocked(ax: number, az: number, bx: number, bz: number, level = 0, skip?: typeof WALLS[number]): boolean {
  const dx = bx - ax, dz = bz - az;
  for (const w of WALLS) if (w !== skip && slabHit(ax, az, dx, dz, w)) return true;
  // ground sight lines also test maze blocks — but only those in the buckets the
  // segment crosses (broadphase), not all ~1600 boxes.
  if (level === 0) { const hits = dynWallsAlong(ax, az, bx, bz); for (let i = 0; i < hits.length; i++) { const w = hits[i]; if (w !== skip && slabHit(ax, az, dx, dz, w)) return true; } }
  return false;
}

/** Slab test: does the segment (ax,az)+(dx,dz)·t, t∈[0,1], overlap box w? */
function slabHit(ax: number, az: number, dx: number, dz: number, w: typeof WALLS[number]): boolean {
  let t0 = 0, t1 = 1;
  for (const [p, d, lo, hi] of [[ax, dx, w.minX, w.maxX], [az, dz, w.minZ, w.maxZ]] as const) {
    if (Math.abs(d) < 1e-6) { if (p < lo || p > hi) return false; }
    else {
      let tn = (lo - p) / d, tf = (hi - p) / d;
      if (tn > tf) { const s = tn; tn = tf; tf = s; }
      t0 = Math.max(t0, tn); t1 = Math.min(t1, tf);
      if (t0 > t1) return false;
    }
  }
  return t1 >= t0;
}

interface Pending {
  type: TankType;
  delay: number;
}

/** The four perimeter inner faces a shell can bank off (WALLS[0..3]), in WORLD
 *  coords. Rebuilt each floor because the interior grows with the layer. */
let BANK_FACES: Array<{ axis: "x" | "z"; c: number; lo: number; hi: number; wall: typeof WALLS[number] }> = [];
/** Refresh per-floor perimeter geometry from the current world bounds. Call once
 *  per layer entry, AFTER setLayerBounds has refreshed WALLS/INTERIOR. */
function refreshArenaGeometry(): void {
  const { minX, maxX, minZ, maxZ } = INTERIOR;
  BANK_FACES = [
    { axis: "x", c: minX, lo: minZ, hi: maxZ, wall: WALLS[0] }, // west
    { axis: "x", c: maxX, lo: minZ, hi: maxZ, wall: WALLS[1] }, // east
    { axis: "z", c: minZ, lo: minX, hi: maxX, wall: WALLS[2] }, // north
    { axis: "z", c: maxZ, lo: minX, hi: maxX, wall: WALLS[3] }, // south
  ];
}

/**
 * One-bounce "bank shot" aim via the mirror trick: reflect the player across a
 * wall face; firing toward that mirror image sends a shell into the wall that
 * ricochets onto the real player. Returns the initial aim direction {x,z} for a
 * valid bank, or null. Used when there's no direct line of sight.
 */
function bankShotDir(ex: number, ez: number, px: number, pz: number): { x: number; z: number } | null {
  for (const f of BANK_FACES) {
    const c = f.c, lo = f.lo, hi = f.hi;
    // the shooter must be on the interior side of this face
    if (f.axis === "x" ? (c < 0 ? ex <= c : ex >= c) : (c < 0 ? ez <= c : ez >= c)) continue;
    const mx = f.axis === "x" ? 2 * c - px : px;
    const mz = f.axis === "z" ? 2 * c - pz : pz;
    const t = f.axis === "x" ? (c - ex) / (mx - ex) : (c - ez) / (mz - ez);
    if (!(t > 0.05 && t < 0.95)) continue;
    const bx = ex + t * (mx - ex);
    const bz = ez + t * (mz - ez);
    const along = f.axis === "x" ? bz : bx;
    if (along < lo + 6 || along > hi - 6) continue; // bounce point on the face (inset from corners)
    // both legs must be unobstructed by OTHER walls (skip the bounce wall itself)
    if (segmentBlocked(ex, ez, bx, bz, 0, f.wall)) continue;
    if (segmentBlocked(bx, bz, px, pz, 0, f.wall)) continue;
    const dx = bx - ex, dz = bz - ez, len = Math.hypot(dx, dz) || 1;
    return { x: dx / len, z: dz / len };
  }
  return null;
}

export class EnemyManager {
  enemies: Tank[] = [];
  allies: Tank[] = []; // hired mercenaries — persist across floors until destroyed
  private pending: Pending[] = [];
  private mutualNames: string[] = [];
  private floorY = 0;
  private spawnQueue: THREE.Vector2[] = []; // arena spawn points for this floor
  /** stats for achievements */
  minesLaidThisLayer = 0;
  private lasers: Lasers;
  private effects: Effects | null = null; // for the laser emitter muzzle-glow (set by Game)
  private layer = 0;
  private nav: Arena | null = null; // current floor's arena — for spawn reachability checks
  private fieldLimit = 99; // max enemies alive on the field at once (rest wait in `pending`)
  private spawnGap = 0; // cooldown between field-gated drip spawns (so refills stagger)
  private allyUp: UpgradeLevers | null = null; // player's run upgrades — mercenaries inherit them
  private enemyUp: UpgradeLevers | null = null; // enemy-team perks (one random non-punch/shield each floor)
  private laserHitPlayer = false; // a laser beam burned the player this frame (drained AFTER the loop, like a shell)
  private _laserPts: number[] = []; // scratch polyline for the laser beam (flat [x,z,...])
  private _lp = { x: 0, z: 0 }; private _lv = { x: 0, z: 0 }; // scratch for the beam trace
  private beams: Array<{ pts: number[]; level: number }> = []; // live laser beams this frame (mercs dodge them)
  private _avoid = { x: 0, z: 0 }; // scratch hazard-avoidance vector
  /** Called when a mercenary dies (any cause) so main can gib it / queue a redeploy. */
  onMercDown: (tank: Tank) => void = () => {};
  /** Called for EVERY tank death (ally or enemy, any cause) with its killer — drives the kill feed. */
  onTankKilled: (victim: Tank, killer?: Tank) => void = () => {};
  private _muzzle = new THREE.Vector3();
  private _allySpot = { x: 0, z: 0 };

  constructor(private scene: THREE.Scene, private geo: Geo, private treads: Treads) {
    this.lasers = new Lasers(scene);
  }

  /** Give the enemy manager the shared effects pool (for the laser emitter muzzle-glow). */
  setEffects(effects: Effects): void {
    this.effects = effects;
  }

  /** The player's mutuals: each enemy becomes one of them (coloured by name). */
  setMutualNames(names: string[]): void {
    this.mutualNames = names;
  }

  get hasMutuals(): boolean {
    return this.mutualNames.length > 0;
  }

  get aliveCount(): number {
    return this.enemies.length + this.pending.length;
  }
  get onFieldCount(): number {
    return this.enemies.length;
  }
  get pendingCount(): number {
    return this.pending.length;
  }
  get allyCount(): number {
    return this.allies.length;
  }

  clear(): void {
    for (const e of this.enemies) { e.removeFromScene(this.scene); e.dispose(); }
    this.enemies = [];
    this.pending = [];
    this.lasers.clear();
    sfx.laserHum(false); // no lasers left — kill the sustained hum
  }

  /** Queue a fresh layer's roster, staggered. Each rises at an open maze cell. In CARD MODE
   *  `half` restricts spawn cells to the enemy side (nearer the exit hole). */
  spawnLayer(layer: number, floorY: number, arena: Arena | null, playerPos: THREE.Vector3, half?: { entryX: number; entryZ: number; exitX: number; exitZ: number }): void {
    this.clear();
    refreshArenaGeometry(); // perimeter bank faces for THIS floor's (world) interior
    this.nav = arena; // remembered so spawn-vetting can reject unreachable (walled-off) cells
    this.layer = layer; // remembered so Spawners can roll level-appropriate reinforcements
    this.floorY = floorY;
    this.minesLaidThisLayer = 0;
    // every 3rd floor is a MONO-TYPE round (one attack-capable type fills the field); else the normal mix
    const mono = !!monoRosterForLayer(layer);
    const roster = monoRosterForLayer(layer) ?? enemiesForLayer(layer);
    this.fieldLimit = enemyFieldLimit(layer); // how many may be alive at once; rest drip-feed
    this.spawnGap = 0;
    this.spawnQueue = arena ? arena.spawnPoints(roster.length, playerPos, half) : [];
    let delay = 0.4;
    // MECHA schedule: debut on wave 10/14, then >=1 walker per non-mono wave, ramping to ALL eligible by wave 100
    const plan = mono ? { forceOne: false, p: 0 } : mechaPlan(layer);
    let mechaCount = 0, firstEligible = -1;
    const entries: { type: TankType; delay: number }[] = [];
    for (let i = 0; i < roster.length; i++) {
      const type = roster[i];
      const eligible = mechaEligible(type);
      if (eligible && firstEligible < 0) firstEligible = i;
      const roll = eligible && plan.p > 0 && Math.random() < plan.p;
      if (roll) mechaCount++;
      // clone a NEW object on the roll — never mutate the shared config roster (would leak to later floors)
      entries.push({ type: roll ? { ...type, mecha: true } : type, delay });
      delay += 0.5;
    }
    // guarantee at least one walker on forced waves (debut + every non-mono wave 15+)
    if (plan.forceOne && mechaCount === 0 && firstEligible >= 0)
      entries[firstEligible] = { type: { ...roster[firstEligible], mecha: true }, delay: entries[firstEligible].delay };
    for (const e of entries) this.pending.push(e);
    // (hired mercenaries are re-deployed fresh each floor by Game.enterLayer from the
    // owned count, so they always come back — no survivor carry-over needed here.)
  }

  /** CARD MODE deploy phase: surface the initial on-field wave (up to fieldLimit) but FREEZE it
   *  (riseTime sentinel) so the player can study the enemy line + place tanks before combat.
   *  The rest stay in `pending` and drip normally once combat starts. */
  flushInitialWave(playerPos: THREE.Vector3): void {
    while (this.enemies.length < this.fieldLimit && this.pending.length) {
      this.spawnOne(this.pending.shift()!.type, playerPos);
    }
    for (const e of this.enemies) { e.riseTime = 1e9; e.setRiseVisual(0); e.sync(); } // pop up + freeze AI (update skips riseTime>0)
  }

  /** Release the frozen deploy-phase wave into combat (START round). */
  releaseInitialWave(): void {
    for (const e of this.enemies) if (e.riseTime > 1e8) e.riseTime = 0.0001;
  }

  /** Hire one mercenary near the player (bought in the round-end shop). Team 0 = friendly.
   *  `front` spawns it on the open approach in front of the elevator (off the pit); the
   *  title-screen demo passes false to spawn its solo merc right at the entry. */
  spawnAlly(playerPos: THREE.Vector3, floorY: number, front = true): void {
    const t = new Tank(this.geo, TANK_TYPES.ally, "Mercenary", false);
    t.team = 0;
    t.level = 0;
    t.floorY = floorY;
    // navigable ground near the player (the shop can open with the player on the entry pad)
    this.clearSpotNear(playerPos.x, playerPos.z, this.allies.length * 2.4, this._allySpot, front);
    t.pos.set(this._allySpot.x, floorY, this._allySpot.z);
    t.treadX = t.pos.x; t.treadZ = t.pos.z;
    t.bodyYaw = yawToward(playerPos.x - t.pos.x, playerPos.z - t.pos.z);
    t.turretYaw = t.bodyYaw; t.wanderYaw = t.bodyYaw;
    t.riseTime = 0.8; t.fireTimer = 0.6;
    for (let s = 0; s < (this.allyUp?.shieldHits ?? 0); s++) t.grantShield(99); // inherit the player's Bulwark shields
    t.addToScene(this.scene);
    t.sync();
    this.allies.push(t);
  }

  /** CARD MODE: deploy an ally tank of ANY type at a grid cell, boosted by its card LEVEL
   *  (+1 shield, faster move/fire/shot per level). Team 0 — fights on the player's side via
   *  the merc AI. Returns the spawned tank. */
  deployAlly(typeId: string, level: number, x: number, z: number, floorY: number, mecha = false): Tank | null {
    const base = TANK_TYPES[typeId];
    if (!base) return null;
    const k = Math.max(0, level - 1);
    const type: TankType = {
      ...base,
      health: base.health + k,                       // +1 absorbable hit (shield badge) per level
      moveSpeed: base.moveSpeed * (1 + 0.05 * k),
      fireInterval: base.fireInterval / (1 + 0.07 * k),
      bulletSpeed: base.bulletSpeed * (1 + 0.03 * k),
      mecha: mecha && mechaEligible(base) ? true : base.mecha, // card mecha toggle (eligible types only)
    };
    const t = new Tank(this.geo, type, base.name, false);
    t.team = 0;
    t.level = 0;
    t.cardLevel = level; // remembered so a Hatchery scales its minions by its card level
    t.floorY = floorY;
    t.pos.set(x, floorY, z);
    t.treadX = x; t.treadZ = z;
    t.bodyYaw = yawToward(-x, -z); t.turretYaw = t.bodyYaw; t.wanderYaw = t.bodyYaw; // face arena centre / the enemy side
    t.riseTime = 0.5; t.fireTimer = 0.3;
    t.addToScene(this.scene);
    t.sync();
    this.allies.push(t);
    return t;
  }

  /** Remove every mercenary (new run / game over). */
  clearAllies(): void {
    for (const a of this.allies) { a.removeFromScene(this.scene); a.dispose(); }
    this.allies = [];
  }

  /** The player's run upgrades — mercenaries inherit them (fire rate, shells, speed, punch, shields). */
  setAllyUpgrades(up: UpgradeLevers): void { this.allyUp = up; }
  /** The enemy team's accumulated perks (one random non-punch/shield upgrade per floor). */
  setEnemyUpgrades(up: UpgradeLevers | null): void { this.enemyUp = up; }

  /** Did a laser beam burn the player this frame? (main drains a life AFTER the update,
   *  deferred like a shell hit so a floor-restart can't corrupt the in-progress loop.) */
  consumePlayerLaser(): boolean { const h = this.laserHitPlayer; this.laserHitPlayer = false; return h; }

  /** Min distance from world (x,z) to the laser polyline in `_laserPts` (n points). */
  private nearLaser(x: number, z: number, n: number): number {
    const p = this._laserPts; let best = Infinity;
    for (let i = 0; i < n - 1; i++) {
      const ax = p[i * 2], az = p[i * 2 + 1], bx = p[(i + 1) * 2], bz = p[(i + 1) * 2 + 1];
      const dx = bx - ax, dz = bz - az, len2 = dx * dx + dz * dz;
      let t = len2 > 1e-6 ? ((x - ax) * dx + (z - az) * dz) / len2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const ex = x - (ax + t * dx), ez = z - (az + t * dz);
      const d = ex * ex + ez * ez;
      if (d < best) best = d;
    }
    return Math.sqrt(best);
  }

  /** Trace a beam from (mx,mz) along (dx,dz), bouncing `bounces` times, into `_laserPts`.
   *  Returns the point count. Mirrors a shell's ricochet path so the beam reads true. */
  private traceLaser(mx: number, mz: number, dx: number, dz: number, level: number, bounces: number): number {
    const pts = this._laserPts, p = this._lp, v = this._lv;
    const len = Math.hypot(dx, dz) || 1;
    p.x = mx; p.z = mz; v.x = dx / len; v.z = dz / len;
    let n = 0; pts[0] = mx; pts[1] = mz; n = 1;
    let bl = bounces, traveled = 0; const step = 6, RANGE = 900;
    while (traveled < RANGE && n < 11) {
      p.x += v.x * step; p.z += v.z * step; traveled += step;
      let hit = reflectCircle(p, v, BULLET.radius, level === 0);
      if (this.nav && this.nav.reflectShell(p, v, BULLET.radius, level)) hit = true;
      if (hit) { pts[n * 2] = p.x; pts[n * 2 + 1] = p.z; n++; if (--bl < 0) break; }
    }
    pts[n * 2] = p.x; pts[n * 2 + 1] = p.z; n++;
    return n;
  }

  /** Mercenary self-preservation: sum a steering vector AWAY from imminent hazards on the
   *  merc's level — enemy mine rings + artillery landing zones (radial repulsion), incoming
   *  shells/missiles (perpendicular sidestep off their path), and laser beams (step off the
   *  line). The turret keeps aiming/firing; only the body dodges. Output in `out`. */
  private hazardAvoid(a: Tank, bullets: Bullets, out: { x: number; z: number }): void {
    out.x = 0; out.z = 0;
    const R = TANK.radius;
    for (const m of bullets.mines) {
      if (m.dead || m.level !== a.level || m.owner.team === a.team) continue; // only enemy mines threaten it
      const dx = a.pos.x - m.pos.x, dz = a.pos.z - m.pos.z, d = Math.hypot(dx, dz), danger = MINE.radius + 8; // just outside the blast
      if (d < danger && d > 1e-3) { const w = (danger - d) / danger; out.x += (dx / d) * w; out.z += (dz / d) * w; }
    }
    for (const s of bullets.shells) {
      if (s.dead || s.level !== a.level || s.team === a.team) continue; // own/ally fire is harmless
      if (s.arc) { // artillery landing zone — flee the telegraphed ring
        const dx = a.pos.x - s.arc.tx, dz = a.pos.z - s.arc.tz, d = Math.hypot(dx, dz), danger = ARC_BLAST + 10;
        if (d < danger && d > 1e-3) { const w = (danger - d) / danger; out.x += (dx / d) * w * 1.2; out.z += (dz / d) * w * 1.2; }
        continue;
      }
      const rvx = a.pos.x - s.pos.x, rvz = a.pos.z - s.pos.z, rv2 = rvx * rvx + rvz * rvz;
      if (rv2 > 620 * 620) continue; // too far to matter yet
      const sv = Math.hypot(s.vel.x, s.vel.z); if (sv < 40) continue; // slow/stationary
      const dirx = s.vel.x / sv, dirz = s.vel.z / sv;
      // (A) NEAR-FIELD: a shell on a near-direct, imminent course → a tight sidestep across it
      if (rv2 <= 130 * 130) {
        const along = rvx * dirx + rvz * dirz; // shell heading toward the merc's side?
        if (along > 0 && along <= 95) {
          const perpx = rvx - along * dirx, perpz = rvz - along * dirz, perp = Math.hypot(perpx, perpz), clear = R + 8; // ~a direct hit
          if (perp < clear) {
            const w = (1 - perp / clear) * (1 - along / 95);
            if (perp > 1e-3) { out.x += (perpx / perp) * w * 1.8; out.z += (perpz / perp) * w * 1.8; }
            else { out.x += -dirz * w * 1.8; out.z += dirx * w * 1.8; } // dead-on — pick a side
          }
        }
      }
      // (B) PREDICTIVE: project the shell's FORWARD PATH (incl. ricochets) and slide off the
      //     line before it arrives — so a merc reads where a shot is HEADING, not just where it
      //     is. A gentle, early nudge (secondary to the tight near-field dodge above).
      const tp = _trajP, tv = _trajV;
      tp.x = s.pos.x; tp.z = s.pos.z; tv.x = dirx; tv.z = dirz;
      const maxB = s.homing ? 0 : (s.bouncesLeft ?? 0); // homing steers — can't predict past its current heading
      const step = 8, RANGE = 720; // look ~the next stretch of flight ahead (covers imminent ricochets)
      let travel = 0, bounces = 0, bestD2 = Infinity, cx = tp.x, cz = tp.z, bestT = RANGE;
      while (travel < RANGE) {
        tp.x += tv.x * step; tp.z += tv.z * step; travel += step;
        const ex = a.pos.x - tp.x, ez = a.pos.z - tp.z, d2 = ex * ex + ez * ez;
        if (d2 < bestD2) { bestD2 = d2; cx = tp.x; cz = tp.z; bestT = travel; }
        let hit = reflectCircle(tp, tv, BULLET.radius, a.level === 0);
        if (this.nav && this.nav.reflectShell(tp, tv, BULLET.radius, a.level)) hit = true;
        if (hit) { if (++bounces > maxB) break; }
      }
      const cd = Math.sqrt(bestD2), pdanger = R + 14; // the predicted path grazes the merc?
      if (cd < pdanger) {
        const soon = 1 - bestT / RANGE; // closer in time → stronger
        const w = (1 - cd / pdanger) * (0.35 + 0.65 * soon) * 0.9; // secondary to the 1.8 near-field weight
        if (cd > 1e-3) { out.x += ((a.pos.x - cx) / cd) * w; out.z += ((a.pos.z - cz) / cd) * w; } // slide off the line
        else { out.x += -dirz * w; out.z += dirx * w; } // dead-on the path — pick a side (perp to the shot)
      }
    }
    for (const b of this.beams) { // laser beams — step off the nearest segment
      if (b.level !== a.level) continue;
      const p = b.pts; let bd = Infinity, cx = 0, cz = 0;
      for (let i = 0; i < p.length - 2; i += 2) {
        const sx = p[i], sz = p[i + 1], ex = p[i + 2], ez = p[i + 3], ddx = ex - sx, ddz = ez - sz, len2 = ddx * ddx + ddz * ddz;
        let t = len2 > 1e-6 ? ((a.pos.x - sx) * ddx + (a.pos.z - sz) * ddz) / len2 : 0; t = t < 0 ? 0 : t > 1 ? 1 : t;
        const px = sx + t * ddx, pz = sz + t * ddz, qx = a.pos.x - px, qz = a.pos.z - pz, d2 = qx * qx + qz * qz;
        if (d2 < bd) { bd = d2; cx = px; cz = pz; }
      }
      const d = Math.sqrt(bd), danger = R + 12;
      if (d < danger && d > 1e-3) { const w = (danger - d) / danger; out.x += ((a.pos.x - cx) / d) * w * 1.5; out.z += ((a.pos.z - cz) / d) * w * 1.5; }
    }
  }

  /** Nearest living threat (player or mercenary) to an enemy — what it hunts + aims at. */
  private nearestThreat(e: Tank, threats: ReadonlyArray<Tank>): Tank | null {
    let best: Tank | null = null, bestD = Infinity;
    for (const t of threats) {
      if (!t.alive || t.riding || t.thrown || t.riseTime > 0) continue;
      const d = (t.pos.x - e.pos.x) ** 2 + (t.pos.z - e.pos.z) ** 2;
      if (d < bestD) { bestD = d; best = t; }
    }
    return best;
  }

  /** Nearest living, same-level enemy to a friendly tank (for ally targeting). */
  private nearestEnemy(from: Tank): Tank | null {
    let best: Tank | null = null, bestD = Infinity;
    for (const e of this.enemies) {
      if (!e.alive || e.riding || e.thrown || e.riseTime > 0 || e.level !== from.level) continue;
      const d = (e.pos.x - from.pos.x) ** 2 + (e.pos.z - from.pos.z) ** 2;
      if (d < bestD) { bestD = d; best = e; }
    }
    return best;
  }

  /** Nearest living enemy on ANY level (a mecha ally can shoot tanks up on roads). */
  private nearestEnemyAnyLevel(from: Tank): Tank | null {
    let best: Tank | null = null, bestD = Infinity;
    for (const e of this.enemies) {
      if (!e.alive || e.riding || e.thrown || e.riseTime > 0) continue;
      const d = (e.pos.x - from.pos.x) ** 2 + (e.pos.z - from.pos.z) ** 2;
      if (d < bestD) { bestD = d; best = e; }
    }
    return best;
  }

  /** Drive the hired mercenaries — they fight EXACTLY like enemies, just on the
   *  player's team: pathfind to the nearest enemy through the maze via the ALLY
   *  flow field (lift edges included, so they ride elevators between decks), swivel
   *  the turret toward the nearest same-level enemy and hold, and bank shells off
   *  walls when there's no clear line. Dead mercs are removed. */
  updateAllies(dt: number, player: Tank, bullets: Bullets, arena: Arena | null, holdIdle = false): void {
    if (this.allies.length === 0) return; // no mercenaries hired — skip the ally-flow BFS entirely
    // BFS the ally flow toward every enemy (any level — lift edges route up/down)
    if (arena) {
      const seeds: { x: number; z: number; level: number }[] = [];
      for (const e of this.enemies) {
        if (!e.alive || e.riding || e.thrown || e.riseTime > 0) continue;
        seeds.push({ x: e.pos.x, z: e.pos.z, level: e.level });
      }
      arena.updateAllyFlow(seeds);
    }
    let allyBankBudget = 4; // shared per-frame bank-shot solve budget (cheap, throttled)
    // mercenaries inherit the player's run upgrades (speed, fire rate, shells, bounces, punch)
    const u = this.allyUp;
    const upSpeed = u ? u.speedMul : 1, upTurn = u ? u.turnSpeedMul : 1, upShell = u ? u.shellSpeedMul : 1;
    const upCool = u ? u.cooldownMul : 1, upDmg = u ? u.damage : 1;
    const upBounce = u ? u.maxBounces - BULLET.maxBounces : 0, upShells = u ? u.maxShells - BULLET.maxPlayerShells : 0;

    for (let i = this.allies.length - 1; i >= 0; i--) {
      const a = this.allies[i];
      if (!a.alive) { this.onTankKilled(a, a.killer); this.onMercDown(a); a.removeFromScene(this.scene); a.dispose(); this.allies.splice(i, 1); continue; }
      a.tickShield(dt);
      if (a.thrown) { a.advanceThrow(dt / SIM_SPEED); continue; }
      if (a.riding) { a.advanceRide(dt / SIM_SPEED); continue; }
      if (a.riseTime > 0) {
        a.riseTime -= dt;
        const t = Math.max(0, Math.min(1, 1 - a.riseTime / 0.8));
        a.setRiseVisual(18 * (1 - t));
        if (a.riseTime <= 0) a.sync();
        continue;
      }

      const tgt = this.nearestEnemy(a); // nearest SAME-LEVEL enemy — what it aims + shoots at
      const distT = tgt ? Math.hypot(tgt.pos.x - a.pos.x, tgt.pos.z - a.pos.z) : Infinity;

      // MECHA ally: the walker AI (roam over walls, crouch/raise to fire its turret, topple).
      // Targets enemies on ANY level — it can shoot tanks up on roads.
      if (a.isMecha) { this.mechaTick(a, this.nearestEnemyAnyLevel(a), dt, bullets, arena); continue; }

      // CARD MODE: special-weapon allies run their TYPE's own AI (god-aim bank, arc lob,
      // homing, beam, charge-and-explode) instead of the shell brawler below. Normal-mode
      // mercs are always the plain "ally" type, so they fall through unchanged.
      const ty = a.type!;
      if (ty.aimBounces || ty.artillery || ty.mlrs || ty.tow || ty.laserBeam || ty.kamikaze) {
        this.cardAllyCombat(a, tgt, dt, bullets, arena);
        continue;
      }
      // CARD MODE v2: SUPPORT allies run their own summon/shield/throw kit (team-aware).
      if (ty.spawner || ty.shieldGen || ty.thrower) {
        this.cardAllySupport(a, tgt, dt, bullets, arena);
        continue;
      }
      // ABOMINATION: fires its grab-bag of abilities on a think timer, then FALLS THROUGH
      // to the shell brawler below for its chase + cannon (mirrors the enemy version).
      if (ty.abomination) {
        a.thinkTimer -= dt;
        if (a.thinkTimer <= 0) { this.allyAbominationAbility(a, tgt, bullets); a.thinkTimer = 1.6 + Math.random() * 1.4; }
      }

      // crowd awareness: how many same-level enemies are pressing in (drives how wide
      // the merc orbits — more shooters ⇒ more room to dodge)
      let crowd = 0;
      for (const o of this.enemies) {
        if (!o.alive || o.riding || o.thrown || o.riseTime > 0 || o.level !== a.level) continue;
        const dx = o.pos.x - a.pos.x, dz = o.pos.z - a.pos.z;
        if (dx * dx + dz * dz < 190 * 190) crowd++;
      }
      // clear line to the target? (decides whether to orbit-and-fire or close in)
      const losClear = !!tgt && !segmentBlocked(a.pos.x, a.pos.z, tgt.pos.x, tgt.pos.z, a.level);

      // --- driving: follow the ally flow field toward the enemies (rides lifts), but
      //     ORBIT a same-level target it has a clear shot on (a moving target is far
      //     harder to focus down). With no line, it CLOSES IN via the flow (routing
      //     around walls) until the shot opens — so stragglers can't hide in corners. ---
      let tx: number, tz: number, moveScale = 1, rideNow = false;
      if (arena) {
        arena.allyFlowStep(a.pos.x, a.pos.z, a.level, FLOW_OUT);
        if (FLOW_OUT.kind === "lift") { rideNow = true; tx = 0; tz = 0; }
        else if (tgt && distT < 320 && losClear) {
          // engaged with a clear shot — ALWAYS keep moving (a still tank gets focus-
          // fired down) and ALWAYS circle-strafe rather than flee (backing straight
          // away just pins it against a wall). The orbit radius widens with the crowd:
          // tight on a lone straggler (reliable LOS to finish it), wide when swarmed
          // (more room to dodge) — but it keeps circling + firing the whole time.
          const dx = tgt.pos.x - a.pos.x, dz = tgt.pos.z - a.pos.z, inv = 1 / Math.max(1, distT);
          const band = crowd >= 4 ? 215 : crowd >= 2 ? 155 : 95;
          const radial = Math.max(-1, Math.min(1, (distT - band) * 0.02));
          tx = -dz * inv + dx * inv * radial;
          tz = dx * inv + dz * inv * radial;
        }
        else if (FLOW_OUT.kind === "move") { tx = FLOW_OUT.dx; tz = FLOW_OUT.dz; }
        else if (tgt) { tx = tgt.pos.x - a.pos.x; tz = tgt.pos.z - a.pos.z; }
        else if (holdIdle) { moveScale = 0; tx = -Math.sin(a.bodyYaw); tz = -Math.cos(a.bodyYaw); } // card mode: no target → HOLD where placed (don't rally to the parked anchor)
        else { tx = player.pos.x - a.pos.x; tz = player.pos.z - a.pos.z; if (tx * tx + tz * tz < 90 * 90) moveScale = 0; }
      } else if (tgt) { tx = tgt.pos.x - a.pos.x; tz = tgt.pos.z - a.pos.z; }
      else if (holdIdle) { moveScale = 0; tx = -Math.sin(a.bodyYaw); tz = -Math.cos(a.bodyYaw); }
      else { tx = player.pos.x - a.pos.x; tz = player.pos.z - a.pos.z; if (tx * tx + tz * tz < 90 * 90) moveScale = 0; }

      // step onto a lift and ride it toward the enemies' level
      if (rideNow && arena) {
        arena.cellCenter(a.pos.x, a.pos.z, RIDE_CELL);
        const toLevel = FLOW_OUT.toLevel;
        a.startRide(toLevel, a.floorY, this.floorY + arena.levelHeight(toLevel), RIDE_CELL.x, RIDE_CELL.z);
        a.sync();
        continue;
      }

      // wedged against geometry? (rare now the flow routes around the elevator curbs.)
      // A persistent wedge breaks for open space (arena centre, swept side to side); a
      // brief one veers ~90° to slide along the wall.
      if (a.stuckT > 1.1 && moveScale > 0) {
        const sb = Math.floor(a.stuckT * 1.5) % 2 ? 1 : -1;
        const cx = -a.pos.x, cz = -a.pos.z;
        tx = cx - cz * sb * 0.6; tz = cz + cx * sb * 0.6;
      } else if (a.blockT > 0 && moveScale > 0) {
        tx = -a.blockNZ * a.blockSide; tz = a.blockNX * a.blockSide; // glide along the wall/wedge it's pinned on
      } else if (a.stuckT > 0.4 && moveScale > 0) {
        const s = Math.floor(a.stuckT * 1.5) % 2 ? 1 : -1;
        const nx = -tz * s, nz = tx * s;
        tx = nx; tz = nz;
      }
      // HAZARD DODGING: steer away from imminent mines/artillery/shells/missiles/beams.
      // When a threat is close this dominates the heading and forces the merc to move,
      // so it sidesteps incoming fire while the turret keeps shooting.
      this.hazardAvoid(a, bullets, this._avoid);
      if (this._avoid.x || this._avoid.z) {
        const am = Math.hypot(this._avoid.x, this._avoid.z);
        const il = 1 / (Math.hypot(tx, tz) || 1);
        tx = tx * il + this._avoid.x * 2.0; tz = tz * il + this._avoid.z * 2.0; // dodge blends with — and when near, outweighs — the combat intent
        if (am > 0.3) moveScale = 1; // a real, close threat — never sit still
      }
      a.wanderYaw = yawToward(tx, tz);
      a.bodyYaw = approachAngle(a.bodyYaw, a.wanderYaw, TANK.turnSpeed * 0.9 * upTurn * dt);
      const bx = a.pos.x, bz = a.pos.z; // pre-move position (for stuck detection)
      const mSpeed = a.type!.moveSpeed * upSpeed;
      const fwdX = -Math.sin(a.bodyYaw), fwdZ = -Math.cos(a.bodyYaw);
      const desX = fwdX * mSpeed * moveScale * dt, desZ = fwdZ * mSpeed * moveScale * dt; // intended motion this frame
      a.pos.x += desX;
      a.pos.z += desZ;
      // separation from same-level enemies, other mercs and the player
      const sep = (o: Tank) => {
        if (o === a || o.level !== a.level || o.riding || o.thrown) return;
        const sx = a.pos.x - o.pos.x, sz = a.pos.z - o.pos.z, d2 = sx * sx + sz * sz;
        if (d2 > 1e-3 && d2 < 22 * 22) { const d = Math.sqrt(d2), push = (22 - d) * 0.5 * dt * 4; a.pos.x += (sx / d) * push; a.pos.z += (sz / d) * push; }
      };
      for (const o of this.enemies) sep(o);
      for (const o of this.allies) sep(o);
      if (player !== a) sep(player); // (in the attract demo `player` IS the merc — don't self-repel)
      const preRX = a.pos.x, preRZ = a.pos.z; // position the resolver is about to correct
      resolveCircle(a.pos, TANK.radius, a.level === 0); // ground solids only on the ground
      const pushX = a.pos.x - preRX, pushZ = a.pos.z - preRZ; // contact normal from the resolve push-out
      if (moveScale > 0) slideCircle(a.pos, TANK.radius, preRX, preRZ, desX, desZ, a.level === 0); // glide off slopes/walls instead of pinning
      if (arena) { arena.confine(a.pos, TANK.radius, a.level); a.floorY = this.floorY + arena.levelHeight(a.level); }
      clampInterior(a.pos, TANK.radius); // never let a merc get shoved out of bounds
      // track progress: trying to move but barely going anywhere ⇒ wedged
      const goal = mSpeed * moveScale * dt * 0.4;
      const moved2 = (a.pos.x - bx) ** 2 + (a.pos.z - bz) ** 2;
      if (moveScale > 0 && moved2 < goal * goal) a.stuckT += dt;
      else a.stuckT = 0;
      if (moveScale > 0) noteBlock(a, dt, pushX, pushZ, moved2, goal * goal, tx, tz); else a.blockT -= dt;

      // --- aim: direct if there's a clear line, else bank a shell off the walls ---
      if (tgt) {
        let want: number, canShoot: boolean;
        if (losClear) {
          want = yawToward(tgt.pos.x - a.pos.x, tgt.pos.z - a.pos.z);
          canShoot = true;
        } else if (a.level === 0 && a.type!.bounces >= 1) {
          a.aimTimer -= dt;
          if (a.aimTimer <= 0 && allyBankBudget > 0) {
            allyBankBudget--;
            const bank = bankShotDir(a.pos.x, a.pos.z, tgt.pos.x, tgt.pos.z);
            a.aimValid = !!bank;
            if (bank) { a.aimDirX = bank.x; a.aimDirZ = bank.z; }
            a.aimTimer = 0.35 + Math.random() * 0.25;
          }
          if (a.aimValid) { want = yawToward(a.aimDirX, a.aimDirZ); canShoot = true; }
          else { want = yawToward(tgt.pos.x - a.pos.x, tgt.pos.z - a.pos.z); canShoot = false; }
        } else {
          want = yawToward(tgt.pos.x - a.pos.x, tgt.pos.z - a.pos.z);
          canShoot = false;
        }
        a.turretYaw = approachAngle(a.turretYaw, want, 4.0 * dt); // steady swivel toward the target
        a.fireTimer -= dt;
        const aimed = Math.abs(((a.turretYaw - want + Math.PI * 3) % (Math.PI * 2)) - Math.PI) < 0.16;
        if (a.fireTimer <= 0 && aimed && canShoot && bullets.countShells(a) < a.type!.maxShells + upShells) {
          a.turretYaw += (Math.random() - 0.5) * 2 * a.type!.aimError;
          bullets.fire(a, a.type!.bulletSpeed * upShell, Math.max(0, a.type!.bounces + upBounce), !!a.type!.rocket, BULLET.life, upDmg); // rocket types fire rockets
          a.advanceBarrel();
          sfx.play("fire", a.pos);
          a.turretYaw = want;
          a.fireTimer = a.type!.fireInterval * upCool * (0.8 + Math.random() * 0.5);
        }
      } else {
        a.turretYaw = approachAngle(a.turretYaw, a.bodyYaw, 4.0 * dt); // no target — settle the turret forward
      }

      const tdx = a.pos.x - a.treadX, tdz = a.pos.z - a.treadZ;
      if (tdx * tdx + tdz * tdz >= TREAD_STEP2) { this.treads.drop(a.pos.x, a.pos.z, a.floorY, a.bodyYaw); a.treadX = a.pos.x; a.treadZ = a.pos.z; }
      a.sync();
    }
  }

  /** CARD MODE: per-type combat AI for a SPECIAL-weapon ally (team 0, targeting the enemy
   *  team) — its real weapon, not the shell brawler. Stationary types hold where placed,
   *  kiters kite, kamikaze charges; mobile ones still dodge fire ("smart like mercs"). */
  private cardAllyCombat(a: Tank, tgt: Tank | null, dt: number, bullets: Bullets, arena: Arena | null): void {
    const t = a.type!;
    const dxp = tgt ? tgt.pos.x - a.pos.x : 0, dzp = tgt ? tgt.pos.z - a.pos.z : 0;
    const distT = tgt ? Math.hypot(dxp, dzp) : Infinity;
    const sameLevel = !!tgt && tgt.level === a.level;
    const losClear = !!tgt && sameLevel && !segmentBlocked(a.pos.x, a.pos.z, tgt.pos.x, tgt.pos.z, a.level);

    // ---- MOVEMENT (default: hold where placed) ----
    let tx = -Math.sin(a.bodyYaw), tz = -Math.cos(a.bodyYaw), moveScale = 0;
    if (t.kamikaze && tgt) { tx = dxp; tz = dzp; moveScale = 1; } // charge the nearest foe
    else if (t.aimBounces && t.mobile && a.aimValid) { tx = -dxp; tz = -dzp; } // stalker: HOLD while a bank is solved (accuracy)
    else if ((t.tow || (t.aimBounces && t.mobile)) && arena && tgt) { // kiters back away through the maze
      arena.allyFleeStep(a.pos.x, a.pos.z, a.level, FLOW_OUT);
      if (FLOW_OUT.kind === "move") { tx = FLOW_OUT.dx; tz = FLOW_OUT.dz; moveScale = distT < 360 ? 1 : 0; }
      else { tx = -dxp; tz = -dzp; moveScale = distT < 220 ? 1 : 0; }
    }
    if (moveScale > 0) { // mobile specials dodge incoming fire
      this.hazardAvoid(a, bullets, this._avoid);
      if (this._avoid.x || this._avoid.z) { const il = 1 / (Math.hypot(tx, tz) || 1); tx = tx * il + this._avoid.x * 2; tz = tz * il + this._avoid.z * 2; }
    }
    a.wanderYaw = yawToward(tx, tz);
    a.bodyYaw = approachAngle(a.bodyYaw, a.wanderYaw, TANK.turnSpeed * 0.9 * dt);
    if (moveScale > 0) {
      const spd = t.kamikaze ? TANK.moveSpeed * Math.min(5 + this.layer * 0.05, 10) : t.moveSpeed;
      const fwdX = -Math.sin(a.bodyYaw), fwdZ = -Math.cos(a.bodyYaw);
      a.pos.x += fwdX * spd * moveScale * dt; a.pos.z += fwdZ * spd * moveScale * dt;
    }
    const sep = (o: Tank) => {
      if (o === a || o.level !== a.level || o.riding || o.thrown) return;
      const sx = a.pos.x - o.pos.x, sz = a.pos.z - o.pos.z, d2 = sx * sx + sz * sz;
      if (d2 > 1e-3 && d2 < 22 * 22) { const d = Math.sqrt(d2), push = (22 - d) * 0.5 * dt * 4; a.pos.x += (sx / d) * push; a.pos.z += (sz / d) * push; }
    };
    for (const o of this.enemies) sep(o);
    for (const o of this.allies) sep(o);
    resolveCircle(a.pos, TANK.radius, a.level === 0);
    if (arena) { arena.confine(a.pos, TANK.radius, a.level); a.floorY = this.floorY + arena.levelHeight(a.level); }
    clampInterior(a.pos, TANK.radius);

    // ---- FIRE (per weapon type) ----
    if (t.kamikaze) {
      if (tgt && sameLevel && distT < 24) { bullets.layMine(a, true); a.alive = false; } // instant team-aware blast
    } else if (t.laserBeam) {
      this.allyLaserTick(a, dt, bullets);
    } else if (t.mlrs) {
      if (tgt) {
        if (a.burstLeft > 0) {
          a.fireTimer -= dt;
          if (a.fireTimer <= 0) { const SC = 220; bullets.fireArc(a, tgt.pos.x + (Math.random() * 2 - 1) * SC, tgt.pos.z + (Math.random() * 2 - 1) * SC, tgt.level, tgt.floorY); if (a.burstLeft % 4 === 0) sfx.play("artillery", a.pos); a.burstLeft--; a.fireTimer = 0.09; }
        } else { a.mineTimer -= dt; if (a.mineTimer <= 0) { a.burstLeft = 12; a.fireTimer = 0; a.mineTimer = 4 + Math.random() * 2; } }
      }
    } else if (t.artillery) {
      if (tgt) {
        a.turretYaw = approachAngle(a.turretYaw, yawToward(dxp, dzp), 4 * dt);
        a.fireTimer -= dt;
        if (a.fireTimer <= 0 && bullets.countShells(a) < t.maxShells) { bullets.fireArc(a, tgt.pos.x, tgt.pos.z, tgt.level, tgt.floorY); sfx.play("artillery", a.pos); a.fireTimer = t.fireInterval * (0.8 + Math.random() * 0.5); }
      }
    } else if (t.tow) {
      if (tgt) {
        a.turretYaw = approachAngle(a.turretYaw, yawToward(dxp, dzp), 4 * dt);
        a.fireTimer -= dt;
        if (a.fireTimer <= 0 && sameLevel && bullets.countShells(a) < t.maxShells) { bullets.fireHoming(a, tgt); sfx.play("fire", a.pos); a.fireTimer = t.fireInterval * (0.8 + Math.random() * 0.5); }
      }
    } else if (t.aimBounces && tgt) {
      this.allyBankFire(a, tgt, dt, bullets, losClear);
    }

    const tdx = a.pos.x - a.treadX, tdz = a.pos.z - a.treadZ;
    if (tdx * tdx + tdz * tdz >= TREAD_STEP2) { this.treads.drop(a.pos.x, a.pos.z, a.floorY, a.bodyYaw); a.treadX = a.pos.x; a.treadZ = a.pos.z; }
    a.sync();
  }

  /** Sniper/Stalker ally: god-aim bank (mirrors the enemy aimBounces solver) at an enemy. */
  private allyBankFire(a: Tank, tgt: Tank, dt: number, bullets: Bullets, losClear: boolean): void {
    const t = a.type!;
    let want: number, canShoot = false, precise = false;
    if (losClear) { want = yawToward(tgt.pos.x - a.pos.x, tgt.pos.z - a.pos.z); canShoot = true; }
    else if (a.level === 0) {
      a.aimTimer -= dt;
      if (a.aimScan < 0 && a.aimTimer <= 0) a.aimScan = 0;
      let n = 0;
      while (a.aimScan >= 0 && a.aimScan < BANK_IMAGES.length && n++ < 6) { // a few candidates/frame (throttled by aimTimer)
        const [ii, jj] = BANK_IMAGES[a.aimScan++];
        const ix = imageCoord(ii, tgt.pos.x, INTERIOR.minX, INTERIOR.maxX), iz = imageCoord(jj, tgt.pos.z, INTERIOR.minZ, INTERIOR.maxZ);
        let bx = ix - a.pos.x, bz = iz - a.pos.z; const bl = Math.hypot(bx, bz); if (bl < 1e-3) continue; bx /= bl; bz /= bl;
        const md = -(a.muzzleLocals[0]?.z ?? -15);
        if (validateBankDir(a.pos.x + bx * md, a.pos.z + bz * md, bx, bz, tgt.pos.x, tgt.pos.z, t.bulletSpeed)) { a.aimValid = true; a.aimDirX = bx; a.aimDirZ = bz; a.aimScan = -1; a.aimTimer = 0.5 + Math.random() * 0.3; break; }
      }
      if (a.aimScan >= BANK_IMAGES.length) { a.aimValid = false; a.aimScan = -1; a.aimTimer = 1.0; }
      if (a.aimValid) { want = yawToward(a.aimDirX, a.aimDirZ); canShoot = true; precise = true; }
      else want = yawToward(tgt.pos.x - a.pos.x, tgt.pos.z - a.pos.z);
    } else want = yawToward(tgt.pos.x - a.pos.x, tgt.pos.z - a.pos.z);
    a.turretYaw = approachAngle(a.turretYaw, want, 4 * dt);
    a.fireTimer -= dt;
    const aimGate = precise ? 0.04 : 0.16;
    const aimed = Math.abs(((a.turretYaw - want + Math.PI * 3) % (Math.PI * 2)) - Math.PI) < aimGate;
    let ok = true;
    if (precise && a.fireTimer <= 0 && aimed) { const fx = -Math.sin(want), fz = -Math.cos(want), md = -(a.muzzleLocals[0]?.z ?? -15); ok = validateBankDir(a.pos.x + fx * md, a.pos.z + fz * md, fx, fz, tgt.pos.x, tgt.pos.z, t.bulletSpeed); if (!ok) { a.aimValid = false; a.aimScan = -1; a.aimTimer = 0; } }
    if (a.fireTimer <= 0 && aimed && canShoot && ok && bullets.countShells(a) < t.maxShells) {
      a.turretYaw = want;
      bullets.fire(a, t.bulletSpeed, t.bounces, false, SNIPER_LIFE, 1, precise);
      sfx.play("fire", a.pos);
      a.fireTimer = t.fireInterval * (0.8 + Math.random() * 0.5);
      if (precise && t.mobile) { a.aimValid = false; a.aimScan = -1; a.aimTimer = Math.max(0.2, a.fireTimer - 0.7); } // stalker re-acquires from its next spot
    }
  }

  /** A throttled emitter glow at a laser's beam origin — the beam is continuous, so cap to ~1 flash/0.07s
   *  (charge-tinted red→pink) instead of spawning 60/s. No sparks; reuses the dedicated muzzle-flash pool. */
  private laserMuzzleFx(e: Tank, x: number, y: number, z: number, color: number, dt: number): void {
    e.laserFxT -= dt;
    if (e.laserFxT > 0) return;
    e.laserFxT = 0.07;
    this.effects?.muzzleFlash(x, y, z, color, 6, 0.09, 0, -Math.sin(e.turretYaw), -Math.cos(e.turretYaw), e.floorY);
  }

  /** Laser ally: the charge→beam cycle, burning ENEMIES caught in the live beam. */
  private allyLaserTick(a: Tank, dt: number, bullets: Bullets): void {
    a.laserStun -= dt; a.laserPhaseTimer -= dt;
    if (a.laserPhaseTimer <= 0) {
      if (a.laserPhase === "beam") { a.laserPhase = "charge"; a.laserPhaseTimer = LASER_CHARGE; }
      else { a.laserPhase = "beam"; a.laserPhaseTimer = LASER_BEAM; a.laserDmgT = 0.25; }
    }
    const foe = this.nearestEnemy(a);
    if (foe) a.turretYaw = approachAngle(a.turretYaw, yawToward(foe.pos.x - a.pos.x, foe.pos.z - a.pos.z), 3 * dt);
    const mz = a.muzzle(this._muzzle);
    const bn = this.traceLaser(mz.x, mz.z, -Math.sin(a.turretYaw), -Math.cos(a.turretYaw), a.level, LASER_BOUNCES);
    if (a.laserPhase === "charge") {
      const f = 1 - Math.max(0, a.laserPhaseTimer) / LASER_CHARGE;
      this.lasers.drawPath(this._laserPts, bn, mz.y, f, lerpHex(LASER_RED, LASER_PINK, f), lerpHex(LASER_RED, LASER_CORE, f));
      this.laserMuzzleFx(a, mz.x, mz.y, mz.z, lerpHex(LASER_RED, LASER_PINK, f), dt);
    } else if (a.laserStun <= 0) {
      this.laserMuzzleFx(a, mz.x, mz.y, mz.z, LASER_PINK, dt);
      this.lasers.drawPath(this._laserPts, bn, mz.y);
      this.beams.push({ pts: this._laserPts.slice(0, bn * 2), level: a.level });
      for (const s of bullets.shells) { if (s.dead || s.team === a.team || s.arc || s.level !== a.level) continue; if (this.nearLaser(s.pos.x, s.pos.z, bn) < BULLET.radius + 7) { a.laserStun = 0.25; break; } }
      a.laserDmgT -= dt;
      if (a.laserDmgT <= 0) {
        let burned = false;
        for (const e of this.enemies) {
          if (!e.alive || e.riding || e.thrown || e.riseTime > 0 || e.level !== a.level || e.invulnT > 0) continue;
          if (e.isMecha && !e.crouched) continue; // a striding walker shrugs off the beam (legs only via direct shots)
          if (this.nearLaser(e.pos.x, e.pos.z, bn) > TANK.radius + 5) continue;
          burned = true; e.health -= 1; if (e.health <= 0) { e.alive = false; e.killer = a; }
        }
        if (burned) a.laserDmgT = 0.5;
      }
    }
  }

  /** CARD MODE v2: a SUPPORT ally (Hatchery / Aegis / Hurler) — its summon/shield/throw
   *  kit, team-aware (targets the enemy team, helps the ally team). Hatchery + Hurler hold
   *  where placed; Aegis trails the tank it's warding so the shield link stays in range. */
  private cardAllySupport(a: Tank, _tgt: Tank | null, dt: number, _bullets: Bullets, arena: Arena | null): void {
    const t = a.type!;
    let tx = -Math.sin(a.bodyYaw), tz = -Math.cos(a.bodyYaw), moveScale = 0;
    if (t.shieldGen) { // Aegis: close in on its ward so the invuln link holds
      const ward = this.nearestProtectableAlly(a);
      if (ward) { const d2 = (ward.pos.x - a.pos.x) ** 2 + (ward.pos.z - a.pos.z) ** 2; if (d2 > 90 * 90) { tx = ward.pos.x - a.pos.x; tz = ward.pos.z - a.pos.z; moveScale = 1; } }
    }
    if (moveScale > 0) { this.hazardAvoid(a, _bullets, this._avoid); if (this._avoid.x || this._avoid.z) { const il = 1 / (Math.hypot(tx, tz) || 1); tx = tx * il + this._avoid.x * 2; tz = tz * il + this._avoid.z * 2; } }
    a.wanderYaw = yawToward(tx, tz);
    a.bodyYaw = approachAngle(a.bodyYaw, a.wanderYaw, TANK.turnSpeed * 0.9 * dt);
    if (moveScale > 0) { const fwdX = -Math.sin(a.bodyYaw), fwdZ = -Math.cos(a.bodyYaw); a.pos.x += fwdX * t.moveSpeed * moveScale * dt; a.pos.z += fwdZ * t.moveSpeed * moveScale * dt; }
    const sep = (o: Tank) => {
      if (o === a || o.level !== a.level || o.riding || o.thrown) return;
      const sx = a.pos.x - o.pos.x, sz = a.pos.z - o.pos.z, d2 = sx * sx + sz * sz;
      if (d2 > 1e-3 && d2 < 22 * 22) { const d = Math.sqrt(d2), push = (22 - d) * 0.5 * dt * 4; a.pos.x += (sx / d) * push; a.pos.z += (sz / d) * push; }
    };
    for (const o of this.enemies) sep(o);
    for (const o of this.allies) sep(o);
    resolveCircle(a.pos, TANK.radius, a.level === 0);
    if (arena) { arena.confine(a.pos, TANK.radius, a.level); a.floorY = this.floorY + arena.levelHeight(a.level); }
    clampInterior(a.pos, TANK.radius);

    if (t.spawner) this.allySpawnerTick(a, dt);
    else if (t.shieldGen) this.allyShieldGenTick(a);
    else if (t.thrower) this.allyThrowerTick(a, dt);

    const tdx = a.pos.x - a.treadX, tdz = a.pos.z - a.treadZ;
    if (tdx * tdx + tdz * tdz >= TREAD_STEP2) { this.treads.drop(a.pos.x, a.pos.z, a.floorY, a.bodyYaw); a.treadX = a.pos.x; a.treadZ = a.pos.z; }
    a.sync();
  }

  /** Nearest team-0 combatant worth shielding — a real ally, not another support unit. */
  private nearestProtectableAlly(a: Tank): Tank | null {
    let best: Tank | null = null, bestD = Infinity;
    for (const o of this.allies) {
      if (o === a || !o.alive || o.thrown || o.riding || o.riseTime > 0 || o.level !== a.level) continue;
      const t = o.type;
      if (!t || t.shieldGen || t.spawner || t.thrower || t.ghost || t.kamikaze) continue;
      const d = (o.pos.x - a.pos.x) ** 2 + (o.pos.z - a.pos.z) ** 2;
      if (d < bestD) { bestD = d; best = o; }
    }
    return best;
  }

  /** Mobile non-support shooters this floor can field — the pool a Hatchery/Abomination
   *  spawns ally minions from. Excludes the special types whose AI we don't run as minions. */
  private allyMinionRoster(): TankType[] {
    return enemiesForLayer(this.layer).filter((t) => t.mobile && t.maxShells > 0 &&
      !t.spawner && !t.shieldGen && !t.thrower && !t.abomination &&
      !t.aimBounces && !t.tow && !t.mlrs && !t.artillery && !t.laserBeam && !t.kamikaze);
  }

  /** Pick a minion type scaled by card LEVEL: low cards spawn the weakest chaff, high
   *  cards unlock the floor's tougher shooters (sorted by a rough power proxy). */
  private pickMinionType(cardLevel: number): TankType | null {
    const pool = this.allyMinionRoster();
    if (!pool.length) return null;
    const power = (t: TankType) => t.health + t.maxShells + t.bounces + t.bulletSpeed * 0.02;
    pool.sort((p, q) => power(p) - power(q));
    const frac = cardLevel <= 2 ? 0.4 : cardLevel <= 4 ? 0.7 : 1;
    const top = Math.max(1, Math.ceil(pool.length * frac));
    return pool[(Math.random() * top) | 0];
  }

  /** Spawn a team-0 minion ally of `type` near (x,z) — the ally twin of spawnAt. */
  private spawnAllyAt(type: TankType, x: number, z: number): void {
    const m = TANK.radius + 2;
    x = Math.max(INTERIOR.minX + m, Math.min(INTERIOR.maxX - m, x));
    z = Math.max(INTERIOR.minZ + m, Math.min(INTERIOR.maxZ - m, z));
    const t = new Tank(this.geo, type, type.name, false, this.layer);
    t.team = 0; t.level = 0; t.floorY = this.floorY;
    t.pos.set(x, t.floorY, z); t.treadX = x; t.treadZ = z;
    t.bodyYaw = yawToward(-x, -z); t.turretYaw = t.bodyYaw; t.wanderYaw = t.bodyYaw;
    t.riseTime = 0.8; t.fireTimer = 0.6 + Math.random();
    t.addToScene(this.scene); t.sync();
    this.allies.push(t);
  }

  /** HATCHERY ally: drip level-scaled minions onto the field (soft-capped). */
  private allySpawnerTick(a: Tank, dt: number): void {
    a.mineTimer -= dt; // reuse the mine timer as the spawn cooldown
    if (a.mineTimer > 0 || this.allies.length >= 24) return; // soft ally field cap
    const type = this.pickMinionType(a.cardLevel);
    if (!type) { a.mineTimer = 2; return; }
    const ang = Math.random() * Math.PI * 2;
    this.spawnAllyAt(type, a.pos.x + Math.cos(ang) * 24, a.pos.z + Math.sin(ang) * 24);
    sfx.play("spawn", a.pos);
    a.mineTimer = 4.5 + Math.random() * 3;
  }

  /** AEGIS ally: project invulnerability onto the nearest team-0 combatant + draw the link. */
  private allyShieldGenTick(a: Tank): void {
    const ward = this.nearestProtectableAlly(a);
    if (!ward) return;
    const d2 = (ward.pos.x - a.pos.x) ** 2 + (ward.pos.z - a.pos.z) ** 2;
    if (d2 > 130 * 130) return;
    ward.invulnT = Math.max(ward.invulnT, 0.2);
    this.lasers.link(a.pos.x, a.floorY + 18, a.pos.z, ward.pos.x, ward.floorY + 12, ward.pos.z);
  }

  /** A throw landing near `enemy` — half at it, half a flank off its facing — kept in bounds. */
  private allyThrowDestNearEnemy(enemy: Tank, out: { x: number; z: number }): void {
    if (Math.random() < 0.5) {
      const ang = Math.random() * Math.PI * 2;
      out.x = enemy.pos.x + Math.cos(ang) * 38; out.z = enemy.pos.z + Math.sin(ang) * 38;
    } else {
      const fx = -Math.sin(enemy.bodyYaw), fz = -Math.cos(enemy.bodyYaw), side = Math.random() < 0.5 ? 1 : -1;
      out.x = enemy.pos.x + -fz * side * 62; out.z = enemy.pos.z + fx * side * 62;
    }
    const m = TANK.radius + 6;
    out.x = Math.max(INTERIOR.minX + m, Math.min(INTERIOR.maxX - m, out.x));
    out.z = Math.max(INTERIOR.minZ + m, Math.min(INTERIOR.maxZ - m, out.z));
  }

  /** HURLER ally: fling a nearby throwable ally at the nearest enemy. */
  private allyThrowerTick(a: Tank, dt: number): void {
    a.mineTimer -= dt; // reuse the mine timer as the throw cooldown
    if (a.mineTimer > 0) return;
    const enemy = this.nearestEnemy(a);
    if (!enemy) { a.mineTimer = 1.0; return; }
    let victim: Tank | null = null, best = Infinity;
    for (const o of this.allies) {
      if (o === a || !o.alive || o.thrown || o.riding || o.level !== a.level || !this.isThrowable(o)) continue;
      const d2 = (o.pos.x - a.pos.x) ** 2 + (o.pos.z - a.pos.z) ** 2;
      if (d2 < 230 * 230 && d2 < best) { best = d2; victim = o; }
    }
    if (!victim) { a.mineTimer = 1.0; return; }
    this.allyThrowDestNearEnemy(enemy, this._throwDest);
    victim.throwTo(this._throwDest.x, this._throwDest.z, this.floorY);
    sfx.play("throw", a.pos);
    a.mineTimer = 2.8 + Math.random() * 2;
  }

  /** ABOMINATION ally: one random ability per think tick (it also fires shells via the
   *  brawler). Mirrors the enemy grab-bag, team-aware (target = nearest enemy). */
  private allyAbominationAbility(a: Tank, tgt: Tank | null, bullets: Bullets): void {
    const roll = Math.random();
    if (roll < 0.85 && !tgt) { if (roll >= 0.7 && roll < 0.85) a.grantShield(13); return; } // most abilities need a foe; the self-shield doesn't
    if (roll < 0.25) {
      if (tgt) bullets.fireArc(a, tgt.pos.x, tgt.pos.z, tgt.level, tgt.floorY); // mortar lob
    } else if (roll < 0.5) {
      if (tgt && tgt.level === a.level) bullets.fireHoming(a, tgt); // homing missile
    } else if (roll < 0.7 && this.allies.length < 24) {
      const type = this.pickMinionType(a.cardLevel || 4); // spawn an ally minion
      if (type) { const ang = Math.random() * Math.PI * 2; this.spawnAllyAt(type, a.pos.x + Math.cos(ang) * 26, a.pos.z + Math.sin(ang) * 26); }
    } else if (roll < 0.85) {
      a.grantShield(13); // patch its own armour
    } else if (tgt) {
      let v: Tank | null = null, best = Infinity; // fling a nearby chase ally at the enemy
      for (const o of this.allies) {
        if (o === a || !o.alive || o.thrown || o.riding || o.level !== a.level || !this.isThrowable(o)) continue;
        const d2 = (o.pos.x - a.pos.x) ** 2 + (o.pos.z - a.pos.z) ** 2;
        if (d2 < 230 * 230 && d2 < best) { best = d2; v = o; }
      }
      if (v) { this.allyThrowDestNearEnemy(tgt, this._throwDest); v.throwTo(this._throwDest.x, this._throwDest.z, this.floorY); }
    }
  }

  // ---- MECHA walker AI (enemy + card ally) ------------------------------

  /** Drive one mecha walker (team-agnostic — `tgt` is the foe to engage, or null to roam).
   *  ROAM over walls → CROUCH on line-of-sight → FIRE the turret → RISE → roam. Topples are
   *  resolved + exploded in bullets.update; here we just keep it falling. Stays on level 0. */
  private mechaTick(e: Tank, tgt: Tank | null, dt: number, bullets: Bullets, arena: Arena | null): void {
    if (e.toppling) { e.sync(); return; } // falling — bullets.update handles the blast (legs frozen)
    const t = e.type!;
    const dxp = tgt ? tgt.pos.x - e.pos.x : 0, dzp = tgt ? tgt.pos.z - e.pos.z : 0;
    const distT = tgt ? Math.hypot(dxp, dzp) : Infinity;
    const inRange = !!tgt && distT < MECHA.fireRange; // ANY level — a tall mecha can shoot up onto roads
    const elevated = !!tgt && tgt.level > e.level;    // target up on a road deck
    const losClear = inRange && tgt!.level === e.level && !segmentBlocked(e.pos.x, e.pos.z, tgt!.pos.x, tgt!.pos.z, e.level);
    if (elevated && arena) e.mechaRaiseY = Math.max(0, Math.min(MECHA.maxRaise, arena.levelHeight(tgt!.level) - MECHA.baseRest));

    // while not ducked low, the legs are the weak point — yank a foot aside from a shot about to hit it
    if (e.mechaCrouch < 0.5) this.mechaLegDodge(e, bullets);

    // ARC / homing / support turrets fire WHILE STRIDING (no duck) — they hit roads too (fireArc
    // takes the target level). Direct-fire bullet turrets (shells/rockets/gatling/snipers) duck.
    if (t.artillery || t.mlrs || t.tow || t.spawner || t.shieldGen || t.thrower) {
      if (inRange && tgt) this.mechaFire(e, tgt, dt, bullets, losClear);
      this.mechaWander(e, dt); // mobile battery — keep roaming while it lobs
      e.updateMechaLegs(dt, arena); e.sync();
      return;
    }

    // ground shot: needs LOS (snipers: a valid BANK, no LOS needed). road shot: extend UP, no LOS.
    let canShoot = elevated ? inRange : losClear;
    if (!elevated && t.aimBounces && inRange && !losClear) {
      // god-aim: a perfect MULTI-bounce bank shot, searched incrementally from the MUZZLE (the same
      // solver ground snipers use) — so a sniper mecha CROUCHES + fires bounce shots with NO line of
      // sight (the old one-bounce bankShotDir rarely solved → mechas almost never ducked off-LOS).
      e.aimTimer -= dt;
      if (e.aimScan < 0 && e.aimTimer <= 0) e.aimScan = 0; // begin a fresh scan
      let budget = 6; // candidates this frame (per-mecha; the scan is throttled by aimTimer)
      while (e.aimScan >= 0 && e.aimScan < BANK_IMAGES.length && budget > 0) {
        budget--;
        const [ii, jj] = BANK_IMAGES[e.aimScan++];
        const ix = imageCoord(ii, tgt!.pos.x, INTERIOR.minX, INTERIOR.maxX);
        const iz = imageCoord(jj, tgt!.pos.z, INTERIOR.minZ, INTERIOR.maxZ);
        let dx = ix - e.pos.x, dz = iz - e.pos.z;
        const len = Math.hypot(dx, dz); if (len < 1e-3) continue;
        dx /= len; dz /= len;
        const md = -(e.muzzleLocals[0]?.z ?? -15); // solve from the muzzle, not the centre
        if (validateBankDir(e.pos.x + dx * md, e.pos.z + dz * md, dx, dz, tgt!.pos.x, tgt!.pos.z, t.bulletSpeed)) {
          e.aimValid = true; e.aimDirX = dx; e.aimDirZ = dz; e.aimScan = -1; e.aimTimer = 0.5 + Math.random() * 0.3; break;
        }
      }
      if (e.aimScan >= BANK_IMAGES.length) { e.aimValid = false; e.aimScan = -1; e.aimTimer = 1.0 + Math.random() * 0.5; } // none found — retry
      canShoot = e.aimValid;
    }

    // DODGE the base: only while ducked LOW (a raise pose keeps the base safe up high — legs only)
    if ((e.mechaPhase === "crouch" || e.mechaPhase === "fire") && e.mechaCrouch > 0.4) {
      this.hazardAvoid(e, bullets, this._avoid);
      if (Math.abs(this._avoid.x) + Math.abs(this._avoid.z) > 0.5) { e.mechaPhase = "flee"; e.mechaFleeT = MECHA.fleeTime; }
    }

    // engage = crouch DOWN for a ground target, or extend UP for a road target
    const engage = (sign: number) => elevated
      ? (e.mechaRaise = Math.min(1, e.mechaRaise + sign * dt / MECHA.crouchTime))
      : (e.mechaCrouch = Math.min(1, e.mechaCrouch + sign * dt / MECHA.crouchTime));
    switch (e.mechaPhase) {
      case "roam":
        if (canShoot) { e.mechaPhase = "crouch"; sfx.play("mechacrouch", e.pos); } else this.mechaWander(e, dt); // servo: start lowering/raising to fire
        break;
      case "crouch": {
        const eng = engage(1);
        if (tgt) { e.turretYaw = approachAngle(e.turretYaw, yawToward(dxp, dzp), 3.5 * dt); this.mechaCrouchMove(e, dxp, dzp, dt); }
        if (!canShoot) { e.mechaPhase = "rise"; sfx.play("mechacrouch", e.pos); }
        else if (eng >= 1) { e.mechaPhase = "fire"; e.mechaDwell = MECHA.dwell; }
        break;
      }
      case "fire":
        engage(1);
        e.mechaDwell -= dt;
        if (tgt) { this.mechaFire(e, tgt, dt, bullets, losClear); this.mechaCrouchMove(e, dxp, dzp, dt); }
        if (e.mechaDwell <= 0 || !canShoot) { e.mechaPhase = "rise"; sfx.play("mechacrouch", e.pos); } // servo: start standing back up
        break;
      case "rise":
        e.mechaCrouch = Math.max(0, e.mechaCrouch - dt / MECHA.crouchTime);
        e.mechaRaise = Math.max(0, e.mechaRaise - dt / MECHA.crouchTime);
        if (e.mechaCrouch <= 0 && e.mechaRaise <= 0) e.mechaPhase = "roam";
        break;
      case "flee":
        e.mechaCrouch = Math.max(0, e.mechaCrouch - dt / MECHA.crouchTime);
        e.mechaRaise = Math.max(0, e.mechaRaise - dt / MECHA.crouchTime);
        e.mechaFleeT -= dt;
        this.mechaFleeMove(e, dxp, dzp, dt);
        if (e.mechaFleeT <= 0) e.mechaPhase = "roam";
        break;
    }
    e.updateSpin(dt, e.mechaPhase === "fire" || e.mechaPhase === "crouch"); // gatling barrel spins as it ducks + fires (no-op for other turrets)
    e.updateMechaLegs(dt, arena); // footstep planting (lead/box-rest) + IK targets
    e.sync();
  }

  /** MECHA flee: back away from the threat/target for a moment after a shot menaces the base. */
  private mechaFleeMove(e: Tank, dxp: number, dzp: number, dt: number): void {
    const d = Math.hypot(dxp, dzp) || 1;
    e.wanderYaw = yawToward(-dxp / d, -dzp / d); // away from the target
    e.bodyYaw = approachAngle(e.bodyYaw, e.wanderYaw, TANK.turnSpeed * 0.9 * dt);
    const speed = MECHA.speed * (e.limp ? MECHA.limpMul : 1);
    e.pos.x += -Math.sin(e.bodyYaw) * speed * dt; e.pos.z += -Math.cos(e.bodyYaw) * speed * dt;
    e.walkPhase += speed * dt * 0.06;
    clampInterior(e.pos, MECHA.bodyR);
  }

  /** Free-roam wander toward a random in-bounds point, STRIDING OVER walls (no maze nav,
   *  no wall collision) — only the outer perimeter (clampInterior) stops it. Drives the legs. */
  private mechaWander(e: Tank, dt: number, speed = MECHA.speed): void {
    const dx = e.mechaWanderX - e.pos.x, dz = e.mechaWanderZ - e.pos.z;
    if (dx * dx + dz * dz < 40 * 40 || (e.mechaWanderX === 0 && e.mechaWanderZ === 0)) {
      e.mechaWanderX = INTERIOR.minX + Math.random() * (INTERIOR.maxX - INTERIOR.minX);
      e.mechaWanderZ = INTERIOR.minZ + Math.random() * (INTERIOR.maxZ - INTERIOR.minZ);
    }
    e.wanderYaw = yawToward(e.mechaWanderX - e.pos.x, e.mechaWanderZ - e.pos.z);
    e.bodyYaw = approachAngle(e.bodyYaw, e.wanderYaw, TANK.turnSpeed * 0.7 * dt);
    const sp = speed * (e.limp ? MECHA.limpMul : 1);
    const fwdX = -Math.sin(e.bodyYaw), fwdZ = -Math.cos(e.bodyYaw);
    e.pos.x += fwdX * sp * dt; e.pos.z += fwdZ * sp * dt;
    e.walkPhase += sp * dt * 0.06; // leg cycle paced by distance walked
    clampInterior(e.pos, MECHA.bodyR); // perimeter-bound; NO wall collision (it strides over the maze)
  }

  /** MECHA: slow movement while crouched + firing — close in when far, else circle-strafe the
   *  target (it can keep shooting on the move). */
  private mechaCrouchMove(e: Tank, dxp: number, dzp: number, dt: number): void {
    const d = Math.hypot(dxp, dzp) || 1;
    const mx = d > 240 ? dxp / d : -dzp / d, mz = d > 240 ? dzp / d : dxp / d; // close in / strafe
    e.wanderYaw = yawToward(mx, mz);
    e.bodyYaw = approachAngle(e.bodyYaw, e.wanderYaw, TANK.turnSpeed * 0.6 * dt);
    const sp = MECHA.crouchSpeed * (e.limp ? MECHA.limpMul : 1);
    e.pos.x += -Math.sin(e.bodyYaw) * sp * dt; e.pos.z += -Math.cos(e.bodyYaw) * sp * dt;
    e.walkPhase += sp * dt * 0.06;
    clampInterior(e.pos, MECHA.bodyR);
  }

  /** MECHA: yank a leg aside if a shot is about to hit its foot (legs are the weak point while
   *  striding). Scans incoming enemy fire vs each living foot; steps it perpendicular to clear. */
  private mechaLegDodge(e: Tank, bullets: Bullets): void {
    for (const s of bullets.shells) {
      if (s.dead || s.team === e.team || s.level !== e.level || s.arc) continue;
      const sv = Math.hypot(s.vel.x, s.vel.z); if (sv < 25) continue; // catch slower shells too
      const dx = s.vel.x / sv, dz = s.vel.z / sv;
      for (let i = 0; i < 4; i++) {
        if (!e.legAlive[i]) continue;
        const w = e.legWorld[i];
        const t = (w.x - s.pos.x) * dx + (w.z - s.pos.z) * dz; // along-shell distance to the closest point
        if (t < 0 || t > sv * 0.75) continue; // already past, or > ~0.75s away (react EARLIER)
        const mx = w.x - (s.pos.x + dx * t), mz = w.z - (s.pos.z + dz * t);
        if (mx * mx + mz * mz > (MECHA.legHitR + BULLET.radius + 12) ** 2) continue; // wider graze window
        const perpX = -dz, perpZ = dx, side = (mx * perpX + mz * perpZ) >= 0 ? 1 : -1; // jump the way it already leans
        const tx = Math.max(INTERIOR.minX + 4, Math.min(INTERIOR.maxX - 4, w.x + perpX * side * 36)); // bigger sidestep, clears the path
        const tz = Math.max(INTERIOR.minZ + 4, Math.min(INTERIOR.maxZ - 4, w.z + perpZ * side * 36));
        e.dodgeLeg(i, tx, tz);
      }
    }
  }

  /** Fire the mecha's mounted turret at `tgt` (team-aware: enemy vs card-ally support kits). */
  private mechaFire(e: Tank, tgt: Tank, dt: number, bullets: Bullets, losClear: boolean): void {
    const t = e.type!;
    const ally = e.team === 0;
    if (t.spawner) { e.turretYaw = approachAngle(e.turretYaw, yawToward(tgt.pos.x - e.pos.x, tgt.pos.z - e.pos.z), 3.5 * dt); if (ally) this.allySpawnerTick(e, dt); else this.spawnerTick(e, dt, tgt); return; }
    if (t.shieldGen) { if (ally) this.allyShieldGenTick(e); else this.shieldGenTick(e); return; }
    if (t.thrower) { e.turretYaw = approachAngle(e.turretYaw, yawToward(tgt.pos.x - e.pos.x, tgt.pos.z - e.pos.z), 3.5 * dt); if (ally) this.allyThrowerTick(e, dt); else this.throwerTick(e, dt, tgt); return; }
    // ELEVATED target (up on a road): the tall mecha rears UP and fires its NORMAL turret weapon
    // straight at the road (roads have no railings, so a direct shot reaches). Only true lobbers arc.
    if (tgt.level > e.level) {
      e.turretYaw = approachAngle(e.turretYaw, yawToward(tgt.pos.x - e.pos.x, tgt.pos.z - e.pos.z), 3.5 * dt);
      e.fireTimer -= dt;
      if (e.fireTimer <= 0) {
        e.fireTimer = t.fireInterval * (0.8 + Math.random() * 0.5);
        if (t.artillery || t.mlrs) { bullets.fireArc(e, tgt.pos.x, tgt.pos.z, tgt.level, tgt.floorY); sfx.play("artillery", e.pos); }
        else if (t.tow) { bullets.fireHoming(e, tgt); sfx.play("fire", e.pos); }
        else { bullets.fire(e, t.bulletSpeed, t.bounces, !!t.rocket, BULLET.life, 1, false, tgt.level, tgt.floorY); sfx.play("fire", e.pos); } // direct shot stamped onto the target's deck
      }
      return;
    }
    // GROUND target. SNIPER/STALKER: aim along the solved BANK dir (or direct) + a precise bouncing shot.
    if (t.aimBounces) {
      const wx = losClear ? tgt.pos.x - e.pos.x : e.aimDirX, wz = losClear ? tgt.pos.z - e.pos.z : e.aimDirZ;
      const want = yawToward(wx, wz);
      e.turretYaw = approachAngle(e.turretYaw, want, 4 * dt);
      e.fireTimer -= dt;
      const aimed = Math.abs(((e.turretYaw - want + Math.PI * 3) % (Math.PI * 2)) - Math.PI) < 0.12;
      if (e.fireTimer <= 0 && aimed) {
        if (!losClear) { // re-confirm the bounce path still lands (the target moved) — else re-acquire, don't waste it
          const fx = -Math.sin(want), fz = -Math.cos(want), md = -(e.muzzleLocals[0]?.z ?? -15);
          if (!validateBankDir(e.pos.x + fx * md, e.pos.z + fz * md, fx, fz, tgt.pos.x, tgt.pos.z, t.bulletSpeed)) { e.aimValid = false; e.aimScan = -1; e.aimTimer = 0; return; }
        }
        e.turretYaw = want;
        bullets.fire(e, t.bulletSpeed, t.bounces, false, SNIPER_LIFE, 1, true);
        e.fireTimer = t.fireInterval * (0.8 + Math.random() * 0.5);
        sfx.play("fire", e.pos);
      }
      return;
    }
    e.turretYaw = approachAngle(e.turretYaw, yawToward(tgt.pos.x - e.pos.x, tgt.pos.z - e.pos.z), 3.5 * dt);
    e.fireTimer -= dt;
    if (e.fireTimer > 0) return;
    e.fireTimer = t.fireInterval * (0.8 + Math.random() * 0.5);
    if (t.artillery || t.mlrs) bullets.fireArc(e, tgt.pos.x, tgt.pos.z, tgt.level, tgt.floorY);
    else if (t.tow) bullets.fireHoming(e, tgt);
    else bullets.fire(e, t.bulletSpeed, t.bounces, !!t.rocket, BULLET.life, 1);
    sfx.play("fire", e.pos);
  }

  /** Title-screen demo helper: steer every living mercenary toward a world point via
   *  the ally flow field — riding intra-floor lifts, juking when wedged — with NO
   *  combat. Used by the attract loop to walk the merc to the exit elevator once the
   *  floor is cleared. (Holes are non-navigable, so seed at a point just OFF the pit.) */
  driveAlliesTo(dt: number, destX: number, destZ: number, destLevel: number, arena: Arena | null): void {
    if (this.allies.length === 0 || !arena) return;
    arena.updateAllyFlow([{ x: destX, z: destZ, level: destLevel }]);
    const sp = this.allyUp?.speedMul ?? 1, tn = this.allyUp?.turnSpeedMul ?? 1;
    for (const a of this.allies) {
      if (!a.alive) continue;
      a.tickShield(dt);
      if (a.thrown) { a.advanceThrow(dt / SIM_SPEED); continue; }
      if (a.riding) { a.advanceRide(dt / SIM_SPEED); continue; }
      if (a.riseTime > 0) {
        a.riseTime -= dt;
        const t = Math.max(0, Math.min(1, 1 - a.riseTime / 0.8));
        a.setRiseVisual(18 * (1 - t));
        if (a.riseTime <= 0) a.sync();
        continue;
      }
      arena.allyFlowStep(a.pos.x, a.pos.z, a.level, FLOW_OUT);
      let tx: number, tz: number;
      if (FLOW_OUT.kind === "lift") { // ride an intra-floor lift to reach the exit level
        arena.cellCenter(a.pos.x, a.pos.z, RIDE_CELL);
        const toLevel = FLOW_OUT.toLevel;
        a.startRide(toLevel, a.floorY, this.floorY + arena.levelHeight(toLevel), RIDE_CELL.x, RIDE_CELL.z);
        a.sync();
        continue;
      } else if (FLOW_OUT.kind === "move") { tx = FLOW_OUT.dx; tz = FLOW_OUT.dz; }
      else { tx = destX - a.pos.x; tz = destZ - a.pos.z; } // flow hasn't reached this cell — head straight at the dest
      // wedge-break (mirrors updateAllies): persistent wedge breaks for open space, a brief one veers ~90°
      if (a.stuckT > 1.1) { const sb = Math.floor(a.stuckT * 1.5) % 2 ? 1 : -1; const cx = -a.pos.x, cz = -a.pos.z; tx = cx - cz * sb * 0.6; tz = cz + cx * sb * 0.6; }
      else if (a.blockT > 0) { tx = -a.blockNZ * a.blockSide; tz = a.blockNX * a.blockSide; }
      else if (a.stuckT > 0.4) { const s = Math.floor(a.stuckT * 1.5) % 2 ? 1 : -1; const nx = -tz * s, nz = tx * s; tx = nx; tz = nz; }
      a.wanderYaw = yawToward(tx, tz);
      a.bodyYaw = approachAngle(a.bodyYaw, a.wanderYaw, TANK.turnSpeed * 0.9 * tn * dt);
      const bx = a.pos.x, bz = a.pos.z;
      const mSpeed = a.type!.moveSpeed * sp;
      const fwdX = -Math.sin(a.bodyYaw), fwdZ = -Math.cos(a.bodyYaw);
      const desX = fwdX * mSpeed * dt, desZ = fwdZ * mSpeed * dt;
      a.pos.x += desX; a.pos.z += desZ;
      const preRX = a.pos.x, preRZ = a.pos.z;
      resolveCircle(a.pos, TANK.radius, a.level === 0);
      const pushX = a.pos.x - preRX, pushZ = a.pos.z - preRZ;
      slideCircle(a.pos, TANK.radius, preRX, preRZ, desX, desZ, a.level === 0);
      arena.confine(a.pos, TANK.radius, a.level); a.floorY = this.floorY + arena.levelHeight(a.level);
      clampInterior(a.pos, TANK.radius);
      const goalD = mSpeed * dt * 0.4;
      const moved2 = (a.pos.x - bx) ** 2 + (a.pos.z - bz) ** 2;
      if (moved2 < goalD * goalD) a.stuckT += dt; else a.stuckT = 0;
      noteBlock(a, dt, pushX, pushZ, moved2, goalD * goalD, tx, tz);
      a.turretYaw = approachAngle(a.turretYaw, a.bodyYaw, 4.0 * dt);
      const tdx = a.pos.x - a.treadX, tdz = a.pos.z - a.treadZ;
      if (tdx * tdx + tdz * tdz >= TREAD_STEP2) { this.treads.drop(a.pos.x, a.pos.z, a.floorY, a.bodyYaw); a.treadX = a.pos.x; a.treadZ = a.pos.z; }
      a.sync();
    }
  }

  /** A navigable, REACHABLE point on the open approach in front of a hole at (hx,hz).
   *  Reuses the spawn placer (same scaled nudge + wall/pit/reachability vetting), so the
   *  title-screen demo's walk-to-the-elevator target is guaranteed pathable — never a
   *  spot inside the pit/curb where the ally flow can't propagate. */
  approachPoint(hx: number, hz: number, out: { x: number; z: number }): void {
    this.clearSpotNear(hx, hz, 0, out, true);
  }

  /** Dev tool: drop one enemy of `type` using the NORMAL spawn pipeline — a random
   *  valid, reachable spot anywhere on the floor with the usual exclusion zone around
   *  the player (not crammed next to them). */
  devSpawn(type: TankType, playerPos: THREE.Vector3): void {
    const spot = { x: 0, z: 0 };
    this.randomSpawnPoint(playerPos, spot);
    this.spawnAt(type, spot.x, spot.z, playerPos);
  }

  /** True once the whole roster has spawned and all are destroyed. */
  get layerCleared(): boolean {
    return this.pending.length === 0 && this.enemies.length === 0;
  }

  /** A safe spawn point: clear of the pits AND of every wall/curb (whole tank
   *  footprint), away from the player, not stacked on another enemy. Vets both
   *  queued maze cells and random fallbacks — a maze cell hugging an elevator
   *  curb fails here, so a tank can never spawn clipped inside the hole walls
   *  (the arena grid doesn't know about the gamemap curbs; this check does). */
  private spotValid(x: number, z: number, playerPos: THREE.Vector3): boolean {
    if (isOverSpawnHole(x, z) || isOverElevator(x, z)) return false; // not in a pit
    if (this.nearWall(x, z, TANK.radius + 2)) return false;          // footprint clears walls + hole curbs
    if (this.nav && !this.nav.isReachable(x, z)) return false;       // not in a walled-off pocket it can't drive out of
    if (Math.hypot(x - playerPos.x, z - playerPos.z) < 95) return false; // not on the player
    for (const o of this.enemies) {
      if (Math.hypot(x - o.pos.x, z - o.pos.z) < 26) return false;   // not stacked on another enemy
    }
    return true;
  }

  /** Nearest navigable spot to (cx,cz): off the pits, footprint clear of walls/curbs.
   *  Mercenaries arrive beside the entry "with the player", but the entry hole itself
   *  is a non-navigable pit — dropping them ON it strands them inside the curb (no flow
   *  route out, and enemies can't path onto it). This snaps them to real ground just
   *  outside. `bias` rotates the search so multiple mercs fan to different sides. */
  private clearSpotNear(cx: number, cz: number, bias: number, out: { x: number; z: number }, front = true): void {
    // anchor the search IN FRONT OF the elevator: the entry holes sit near the arena's
    // edge, so nudging the start toward the centre (≈ origin) lands mercs on the open
    // approach — never on the pad/curb or behind the pit. The flow already keeps them
    // off the elevator; this makes sure they never even START on it.
    // The nudge is a FRACTION of the hole's distance from centre (not a fixed offset),
    // so it SCALES with the floor: on a huge late-game arena the hole is far out, and a
    // fixed 78u nudge would leave the merc jammed in the corner — a proportional nudge
    // keeps it on the open approach at any map size.
    let r0 = 52;
    if (front) {
      const d = Math.hypot(cx, cz);
      if (d > 1) { const f = Math.min(0.6, Math.max(78, d * 0.32) / d); cx -= cx * f; cz -= cz * f; }
      r0 = 18;
    }
    for (let r = r0; r <= 190; r += 14) {
      const steps = Math.max(8, Math.round(r / 7));
      for (let i = 0; i < steps; i++) {
        const a = bias + (i / steps) * Math.PI * 2;
        const x = cx + Math.cos(a) * r, z = cz + Math.sin(a) * r;
        if (isOverSpawnHole(x, z) || isOverElevator(x, z)) continue;
        if (this.nearWall(x, z, TANK.radius + 2)) continue;
        if (this.nav && !this.nav.isReachable(x, z)) continue; // never strand a merc in a walled-off pocket
        out.x = x; out.z = z; return;
      }
    }
    out.x = cx; out.z = cz; // give up (shouldn't happen on a real floor)
  }

  /** A random walkable spot: inside the arena, clear of walls/holes, away from
   *  the player (and other enemies). Falls back to a ring around the center. */
  private randomSpawnPoint(playerPos: THREE.Vector3, out: { x: number; z: number }): void {
    const m = TANK.radius + 4;
    const minX = INTERIOR.minX + m, maxX = INTERIOR.maxX - m;
    const minZ = INTERIOR.minZ + m, maxZ = INTERIOR.maxZ - m;
    for (let tries = 0; tries < 80; tries++) {
      const x = minX + Math.random() * (maxX - minX);
      const z = minZ + Math.random() * (maxZ - minZ);
      if (this.spotValid(x, z, playerPos)) { out.x = x; out.z = z; return; }
    }
    // fallback: a point offset from center, opposite the player
    out.x = (playerPos.x > 0 ? -1 : 1) * INTERIOR.maxX * 0.42;
    out.z = (playerPos.z > 0 ? -1 : 1) * INTERIOR.maxZ * 0.42;
  }

  private nearWall(x: number, z: number, r: number): boolean {
    for (const b of WALLS) {
      const cx = Math.max(b.minX, Math.min(x, b.maxX));
      const cz = Math.max(b.minZ, Math.min(z, b.maxZ));
      if ((x - cx) ** 2 + (z - cz) ** 2 < r * r) return true;
    }
    return false;
  }

  private spawnOne(type: TankType, playerPos: THREE.Vector3): void {
    const spot = { x: 0, z: 0 };
    const q = this.spawnQueue.pop(); // Vector2(x = worldX, y = worldZ)
    if (q && this.spotValid(q.x, q.y, playerPos)) { spot.x = q.x; spot.z = q.y; } // vetted maze cell
    else this.randomSpawnPoint(playerPos, spot); // ran out / would clip a curb → find a clear spot
    this.spawnAt(type, spot.x, spot.z, playerPos);
  }

  /** Create one enemy of `type` at world (x,z) on the ground, rising in. Used both
   *  for the layer roster and for Spawner reinforcements. */
  private spawnAt(type: TankType, x: number, z: number, playerPos: THREE.Vector3): void {
    // never place a tank past the perimeter: a Hatchery (or Abomination) hugging the
    // wall spawns reinforcements ±24 away, which could land OUT OF BOUNDS — and a tank
    // that spawns OOB rises in place there (the rise skips the movement clamp), then
    // roamed the void. Clamp the spawn to the interior so every rise starts in-bounds.
    const m = TANK.radius + 2;
    x = Math.max(INTERIOR.minX + m, Math.min(INTERIOR.maxX - m, x));
    z = Math.max(INTERIOR.minZ + m, Math.min(INTERIOR.maxZ - m, z));
    const t = new Tank(this.geo, type, type.name, false, this.layer); // layer picks the turret tier (e.g. TOW barrels)
    t.level = 0; // enemies always rise from the ground
    t.floorY = this.floorY;
    t.pos.set(x, t.floorY, z);
    t.treadX = x; t.treadZ = z; // seed the tread tracker at the spawn
    t.bodyYaw = yawToward(playerPos.x - x, playerPos.z - z); // face the player
    t.turretYaw = t.bodyYaw;
    t.wanderYaw = t.bodyYaw;
    t.riseTime = 0.8;
    t.fireTimer = 1.0 + Math.random() * type.fireInterval * this.fireScale();
    t.thinkTimer = Math.random();
    t.mineTimer = 2 + Math.random() * 3;
    t.aimTimer = type.aimBounces ? 0 : Math.random() * 0.4; // bouncers solve at once; others stagger
    if (this.mutualNames.length) {
      t.setMutual(this.mutualNames[(Math.random() * this.mutualNames.length) | 0]);
    }
    t.addToScene(this.scene);
    t.sync();
    this.enemies.push(t);
  }

  /** Nearest enemy worth shielding: a real same-level combatant — NOT another support
   *  tank, a ghost, or a kamikaze (an invulnerable suicide-bomb would be unfair). */
  private nearestProtectable(e: Tank): Tank | null {
    let best: Tank | null = null, bestD = Infinity;
    for (const o of this.enemies) {
      if (o === e || !o.alive || o.thrown || o.riding || o.riseTime > 0 || o.level !== e.level) continue;
      const t = o.type;
      if (!t || t.shieldGen || t.spawner || t.thrower || t.ghost || t.kamikaze) continue;
      const d = (o.pos.x - e.pos.x) ** 2 + (o.pos.z - e.pos.z) ** 2;
      if (d < bestD) { bestD = d; best = o; }
    }
    return best;
  }

  /** Enemies shoot faster on later floors: scales every fire interval DOWN toward a
   *  0.5× floor (≈2× fire rate) by ~layer 50, then holds. */
  private fireScale(): number { return Math.max(0.5, 1 - this.layer * 0.01); }

  /** A tank a Thrower may hurl: a mobile CHASE/melee type that wants to be near the
   *  player — never the distance-keepers (snipers, kiters, spawners, shield-gens) and
   *  never a kamikaze (dropping an instant-detonating sprinter on the player is
   *  undodgeable). */
  private isThrowable(o: Tank): boolean {
    const t = o.type;
    return !!t && t.mobile && !t.move && !t.spawner && !t.shieldGen && !t.thrower && !t.aimBounces && !t.kamikaze;
  }

  /** A throw destination near the player — half the time right at them, half the
   *  time a flank off to the player's side (where they aren't looking). */
  private throwDest(player: Tank, out: { x: number; z: number }): void {
    if (Math.random() < 0.5) {
      const a = Math.random() * Math.PI * 2; // dropped beside the player
      out.x = player.pos.x + Math.cos(a) * 38; out.z = player.pos.z + Math.sin(a) * 38;
    } else {
      const fx = -Math.sin(player.bodyYaw), fz = -Math.cos(player.bodyYaw); // player forward
      const side = Math.random() < 0.5 ? 1 : -1;
      out.x = player.pos.x + -fz * side * 62; out.z = player.pos.z + fx * side * 62; // perpendicular flank
    }
    // keep the landing IN BOUNDS — a tank thrown past the perimeter gets stuck OOB and softlocks
    const m = TANK.radius + 6;
    out.x = Math.max(INTERIOR.minX + m, Math.min(INTERIOR.maxX - m, out.x));
    out.z = Math.max(INTERIOR.minZ + m, Math.min(INTERIOR.maxZ - m, out.z));
  }

  private spawnerTick(e: Tank, dt: number, target: Tank): void {
    e.mineTimer -= dt; // reuse the mine timer as the spawn cooldown
    if (e.mineTimer > 0 || this.enemies.length >= 18) return; // soft field cap
    const roster = enemiesForLayer(this.layer).filter((t) => t.mobile && !t.spawner && !t.shieldGen && !t.thrower);
    if (!roster.length) { e.mineTimer = 2; return; }
    const t = roster[(Math.random() * roster.length) | 0];
    const a = Math.random() * Math.PI * 2;
    this.spawnAt(t, e.pos.x + Math.cos(a) * 24, e.pos.z + Math.sin(a) * 24, target.pos);
    sfx.play("spawn", e.pos);
    e.mineTimer = 4.5 + Math.random() * 3;
  }

  /** AEGIS: project INVULNERABILITY onto the one ally it's warding (the tank it hides
   *  behind), refreshed every frame, plus a blue shield-link beam. Kill the Aegis to
   *  drop the shield. */
  private shieldGenTick(e: Tank): void {
    const ward = this.nearestProtectable(e);
    if (!ward) return;
    const d2 = (ward.pos.x - e.pos.x) ** 2 + (ward.pos.z - e.pos.z) ** 2;
    if (d2 > 130 * 130) return; // out of projection range — the movement closes in first
    ward.invulnT = Math.max(ward.invulnT, 0.2); // refreshed each frame → invulnerable while linked
    this.lasers.link(e.pos.x, e.floorY + 18, e.pos.z, ward.pos.x, ward.floorY + 12, ward.pos.z);
  }

  private _throwDest = { x: 0, z: 0 };
  private throwerTick(e: Tank, dt: number, target: Tank): void {
    e.mineTimer -= dt; // reuse the mine timer as the throw cooldown
    if (e.mineTimer > 0) return;
    let victim: Tank | null = null, best = Infinity;
    for (const o of this.enemies) {
      if (o === e || !o.alive || o.thrown || o.riding || o.level !== e.level || !this.isThrowable(o)) continue;
      const d2 = (o.pos.x - e.pos.x) ** 2 + (o.pos.z - e.pos.z) ** 2;
      if (d2 < 230 * 230 && d2 < best) { best = d2; victim = o; }
    }
    if (!victim) { e.mineTimer = 1.0; return; } // nothing to throw — try again soon
    this.throwDest(target, this._throwDest);
    victim.throwTo(this._throwDest.x, this._throwDest.z, this.floorY);
    sfx.play("throw", e.pos);
    e.mineTimer = 2.8 + Math.random() * 2;
  }

  /** The Abomination's grab-bag: one random ability per cooldown (it also fires
   *  normal shells in the main loop). Everything the roster can do — except ghost. */
  private abominationAbility(e: Tank, target: Tank, bullets: Bullets): void {
    const roll = Math.random();
    if (roll < 0.25) {
      bullets.fireArc(e, target.pos.x, target.pos.z, target.level, target.floorY); // mortar lob
    } else if (roll < 0.5) {
      bullets.fireHoming(e, target); // homing missile
    } else if (roll < 0.7 && this.enemies.length < 18) {
      const roster = enemiesForLayer(this.layer).filter((t) => t.mobile && !t.spawner && !t.shieldGen && !t.thrower && !t.abomination);
      if (roster.length) { const t = roster[(Math.random() * roster.length) | 0]; const a = Math.random() * Math.PI * 2; this.spawnAt(t, e.pos.x + Math.cos(a) * 26, e.pos.z + Math.sin(a) * 26, target.pos); }
    } else if (roll < 0.85) {
      e.grantShield(13); // patch its own armour back up (it's a damage sponge)
    } else {
      let v: Tank | null = null, best = Infinity; // fling a nearby same-level chase ally at the target
      for (const o of this.enemies) {
        if (o === e || !o.alive || o.thrown || o.riding || o.level !== e.level || !this.isThrowable(o)) continue;
        const d2 = (o.pos.x - e.pos.x) ** 2 + (o.pos.z - e.pos.z) ** 2;
        if (d2 < 230 * 230 && d2 < best) { best = d2; v = o; }
      }
      if (v) { this.throwDest(target, this._throwDest); v.throwTo(this._throwDest.x, this._throwDest.z, this.floorY); }
    }
  }

  /**
   * Drive the AI. `now` is the running clock (seconds). Fires/lays mines through
   * `bullets`. Returns nothing; destruction is handled by the bullets system.
   */
  update(dt: number, human: Tank, humanVel: THREE.Vector3, bullets: Bullets, arena: Arena | null, threats: ReadonlyArray<Tank>): void {
    this.nav = arena; // keep the reachability ref current for mid-floor (pending/spawner) spawns
    this.beams.length = 0; // laser beams are re-registered each frame (mercs read them to dodge)
    // enemy team's accumulated per-floor perks (speed/fire/bounce — never punch/shields)
    const eu = this.enemyUp;
    const euSpeed = eu ? eu.speedMul : 1, euTurn = eu ? eu.turnSpeedMul : 1, euShell = eu ? eu.shellSpeedMul : 1;
    const euCool = eu ? eu.cooldownMul : 1;
    const euBounce = eu ? eu.maxBounces - BULLET.maxBounces : 0, euShells = eu ? eu.maxShells - BULLET.maxPlayerShells : 0;
    // BFS toward the THREATS enemies hunt (player + live mercenaries), throttled
    if (arena) arena.updateFlow(threats.filter((t) => t.alive && !t.riding && !t.thrown && t.riseTime <= 0).map((t) => ({ x: t.pos.x, z: t.pos.z, level: t.level })));
    // release pending spawns — staggered by their delay, but ALSO capped to `fieldLimit`
    // alive at once: once the field is full, the rest wait until the player/mercs thin
    // them out, and a short gap staggers each refill. `spawnOne` keeps spawns off the player.
    for (const p of this.pending) p.delay -= dt;
    this.spawnGap -= dt;
    while (this.pending.length && this.pending[0].delay <= 0 && this.enemies.length < this.fieldLimit && this.spawnGap <= 0) {
      this.spawnOne(this.pending.shift()!.type, human.pos);
      this.spawnGap = 0.45; // one drip per ~0.45s so a wave of kills doesn't pop a whole batch at once
    }

    // global per-frame budget of bank-shot candidate tests, shared by all snipers,
    // so the (incremental) search can never spike a frame no matter how many
    // snipers are on the field or whether any solution exists.
    let bankBudget = 6;

    this.lasers.begin(arena); // laser-sight beams retraced this frame

    // a single sustained wobbling hum covers ALL firing lasers; track the nearest one
    // (to the player) for distance attenuation, then drive sfx.laserHum after the loop.
    let humOn = false, humDist = Infinity, humGain = 1; // humGain scales the volume — ramps up over a laser's charge
    const humPos = { x: 0, y: 0, z: 0 };

    // capture the count so Spawner reinforcements (appended mid-loop) wait until the
    // next frame to act
    const count0 = this.enemies.length;
    for (let _i = 0; _i < count0; _i++) {
      const e = this.enemies[_i];
      e.tickShield(dt); // fade the shield flash + redraw when it ends
      if (e.invulnT > 0) e.invulnT -= dt; // Aegis shield decays unless refreshed this frame
      // being thrown by a Thrower — arc through the air, AI frozen until it lands
      if (e.thrown) { e.advanceThrow(dt / SIM_SPEED); continue; }
      // riding an intra-floor lift — freeze the AI until it lands (ride at real time)
      if (e.riding) { e.advanceRide(dt / SIM_SPEED); continue; }

      if (e.riseTime > 0) {
        e.riseTime -= dt;
        const t = Math.max(0, Math.min(1, 1 - e.riseTime / 0.8));
        e.setRiseVisual(18 * (1 - t));
        if (e.riseTime <= 0) e.sync();
        continue;
      }

      // MECHA: a tall walker runs its own AI — roams OVER walls, crouches on line-of-sight to
      // fire its turret (then the base is the weak point), and topples when 2 legs are downed.
      if (e.isMecha) { this.mechaTick(e, this.nearestThreat(e, threats), dt, bullets, arena); continue; }

      // hunt + aim at the nearest threat (player or mercenary); idle if none left
      const tgt = this.nearestThreat(e, threats);
      if (!tgt) { e.sync(); continue; }
      const tgtVel = tgt === human ? humanVel : ZERO_VEC; // only the player's motion is led
      const dxp = tgt.pos.x - e.pos.x;
      const dzp = tgt.pos.z - e.pos.z;
      const distP = Math.hypot(dxp, dzp);

      // --- driving (mobile types): follow the flow field through the maze ---
      let moved = 0;
      if (e.type!.mobile) {
        let tx: number, tz: number;
        let moveScale = 1;
        let rideNow = false;
        if (e.type!.move === "kite") {
          // KITE to the FARTHEST spot that still has a shot, routing through the maze
          // (fleeStep) rather than reversing into a wall. A god-aim bouncer (Stalker) that
          // has NO firing solution CLOSES IN (flow toward the player) to regain one instead
          // of stranding itself out of range; a TOW (homing) needs no solution, so it just
          // keeps max distance.
          const needsShot = !!e.type!.aimBounces;
          // a shot needs the SAME level as the target (shells are level-locked) + either a
          // fresh line of sight OR a valid bank (bank solves run only on the ground, so the
          // cached aimValid only counts at level 0 — never trust it on an overpass).
          const hasShot = !needsShot || (e.level === tgt.level &&
            ((e.level === 0 && e.aimValid) || !segmentBlocked(e.pos.x, e.pos.z, tgt.pos.x, tgt.pos.z, e.level)));
          if (needsShot && e.aimValid) { moveScale = 0; tx = -dxp; tz = -dzp; } // HOLD STILL while a bank is solved — kiting between solve and fire moved the muzzle off the solved spot and desynced the bounce path (misses). Re-acquires after each shot (below) so it still kites between shots.
          else if (needsShot && !hasShot && arena) {
            arena.flowStep(e.pos.x, e.pos.z, e.level, FLOW_OUT); // no shot — go get one
            if (FLOW_OUT.kind === "lift") { rideNow = true; tx = dxp; tz = dzp; }
            else if (FLOW_OUT.kind === "move") { tx = FLOW_OUT.dx; tz = FLOW_OUT.dz; }
            else { tx = dxp; tz = dzp; }
          } else if (distP >= 460) { moveScale = 0; tx = -dxp; tz = -dzp; } // far + can shoot — hold
          else if (arena) { arena.fleeStep(e.pos.x, e.pos.z, e.level, FLOW_OUT); if (FLOW_OUT.kind === "move") { tx = FLOW_OUT.dx; tz = FLOW_OUT.dz; } else { tx = -dxp; tz = -dzp; } }
          else { tx = -dxp; tz = -dzp; }
        } else if (e.type!.move === "flee") {
          // support tanks: keep a comfortable gap, routing through the maze (no firing)
          if (arena) { arena.fleeStep(e.pos.x, e.pos.z, e.level, FLOW_OUT); if (FLOW_OUT.kind === "move") { tx = FLOW_OUT.dx; tz = FLOW_OUT.dz; } else { tx = -dxp; tz = -dzp; } }
          else { tx = -dxp; tz = -dzp; }
          if (distP >= 260) moveScale = 0;
        } else if (e.type!.move === "shield-ally") {
          // AEGIS: hide behind the ally it protects, on the FAR side from the threat
          // nearest that ally — and PATHFIND there (flow toward the front line) rather
          // than walking straight at the ally into a wall. No ward → flee smartly.
          const ward = this.nearestProtectable(e);
          if (!ward) {
            if (arena) { arena.fleeStep(e.pos.x, e.pos.z, e.level, FLOW_OUT); if (FLOW_OUT.kind === "move") { tx = FLOW_OUT.dx; tz = FLOW_OUT.dz; } else { tx = -dxp; tz = -dzp; } }
            else { tx = -dxp; tz = -dzp; }
            if (distP >= 260) moveScale = 0;
          } else {
            let rx = tgt.pos.x, rz = tgt.pos.z, rb = Infinity; // threat nearest the WARD picks the hide side
            for (const th of threats) { if (!th.alive || th.riding || th.thrown || th.riseTime > 0) continue; const d = (th.pos.x - ward.pos.x) ** 2 + (th.pos.z - ward.pos.z) ** 2; if (d < rb) { rb = d; rx = th.pos.x; rz = th.pos.z; } }
            let bx = ward.pos.x - rx, bz = ward.pos.z - rz; const bl = Math.hypot(bx, bz) || 1; bx /= bl; bz /= bl; // threat→ward = "behind"
            const hideX = ward.pos.x + bx * 30, hideZ = ward.pos.z + bz * 30;
            const dh2 = (hideX - e.pos.x) ** 2 + (hideZ - e.pos.z) ** 2;
            if (dh2 < 220 * 220 && !segmentBlocked(e.pos.x, e.pos.z, hideX, hideZ, e.level)) {
              tx = hideX - e.pos.x; tz = hideZ - e.pos.z; if (dh2 < 12 * 12) moveScale = 0; // clear path — slot in behind
            } else if (arena) {
              arena.flowStep(e.pos.x, e.pos.z, e.level, FLOW_OUT);
              if (FLOW_OUT.kind === "lift") { rideNow = true; tx = dxp; tz = dzp; }
              else if (FLOW_OUT.kind === "move") { tx = FLOW_OUT.dx; tz = FLOW_OUT.dz; }
              else { tx = ward.pos.x - e.pos.x; tz = ward.pos.z - e.pos.z; }
            } else { tx = ward.pos.x - e.pos.x; tz = ward.pos.z - e.pos.z; }
          }
        } else if (e.type!.move === "highground") {
          // artillery: ride a lift UP when on one; else hold at range (its arc clears
          // walls); when too close, retreat THROUGH the maze, not straight into a wall.
          const lift = arena ? arena.liftAt(e.pos.x, e.pos.z, e.level) : null;
          if (lift && lift.toLevel > e.level) { rideNow = true; FLOW_OUT.toLevel = lift.toLevel; tx = dxp; tz = dzp; }
          else if (distP >= 300) { moveScale = 0; tx = -dxp; tz = -dzp; }
          else if (arena) { arena.fleeStep(e.pos.x, e.pos.z, e.level, FLOW_OUT); if (FLOW_OUT.kind === "move") { tx = FLOW_OUT.dx; tz = FLOW_OUT.dz; } else { tx = -dxp; tz = -dzp; } }
          else { tx = -dxp; tz = -dzp; }
        } else if (arena) {
          arena.flowStep(e.pos.x, e.pos.z, e.level, FLOW_OUT);
          if (FLOW_OUT.kind === "lift") {
            rideNow = true; tx = dxp; tz = dzp; // hold heading; the ride snaps us in
          } else if (FLOW_OUT.kind === "move") {
            tx = FLOW_OUT.dx; tz = FLOW_OUT.dz; // route toward the player
          } else if (distP < 70) { tx = dxp + dzp * 0.5; tz = dzp - dxp * 0.5; } // strafe when stuck
          else { tx = dxp; tz = dzp; }
        } else { tx = dxp; tz = dzp; }

        // step onto a lift and ride it toward the player's level
        if (rideNow && arena) {
          arena.cellCenter(e.pos.x, e.pos.z, RIDE_CELL);
          const toLevel = FLOW_OUT.toLevel;
          e.startRide(toLevel, e.floorY, this.floorY + arena.levelHeight(toLevel), RIDE_CELL.x, RIDE_CELL.z);
          e.sync();
          continue;
        }

        if (e.blockT > 0 && moveScale > 0) { tx = -e.blockNZ * e.blockSide; tz = e.blockNX * e.blockSide; } // pinned on a wall/wedge → glide along it
        e.wanderYaw = yawToward(tx, tz);
        e.bodyYaw = approachAngle(e.bodyYaw, e.wanderYaw, TANK.turnSpeed * 0.9 * euTurn * dt);
        const fwdX = -Math.sin(e.bodyYaw), fwdZ = -Math.cos(e.bodyYaw);
        const before = { x: e.pos.x, z: e.pos.z };
        // kamikazes sprint at 5× the player's speed, ramping to a 10× cap by layer 100
        // (an undodgeable-by-driving charger — you have to shoot it or sidestep).
        const spd = (e.type!.kamikaze ? TANK.moveSpeed * Math.min(5 + this.layer * 0.05, 10) : e.type!.moveSpeed) * euSpeed;
        const desX = fwdX * spd * moveScale * dt, desZ = fwdZ * spd * moveScale * dt; // intended motion this frame
        e.pos.x += desX;
        e.pos.z += desZ;
        // separation from other enemies on the same level
        for (const o of this.enemies) {
          if (o === e || o.riseTime > 0 || o.riding || o.thrown || o.level !== e.level) continue;
          const sx = e.pos.x - o.pos.x, sz = e.pos.z - o.pos.z;
          const d2 = sx * sx + sz * sz;
          if (d2 > 1e-3 && d2 < 20 * 20) {
            const d = Math.sqrt(d2);
            const push = (20 - d) * 0.5 * dt * 4;
            e.pos.x += (sx / d) * push;
            e.pos.z += (sz / d) * push;
          }
        }
        const preRX = e.pos.x, preRZ = e.pos.z; // position the resolver is about to correct
        resolveCircle(e.pos, TANK.radius, e.level === 0); // ground solids only on the ground
        const pushX = e.pos.x - preRX, pushZ = e.pos.z - preRZ; // contact normal from the resolve push-out
        if (moveScale > 0) slideCircle(e.pos, TANK.radius, preRX, preRZ, desX, desZ, e.level === 0); // glide off slopes/walls instead of pinning
        if (arena) {
          arena.confine(e.pos, TANK.radius, e.level); // stay on the road deck if elevated
          e.floorY = this.floorY + arena.levelHeight(e.level);
        }
        clampInterior(e.pos, TANK.radius); // never let an enemy get shoved out of bounds (e.g. crowding the perimeter)
        moved = Math.hypot(e.pos.x - before.x, e.pos.z - before.z);
        const eGoal = spd * moveScale * dt * 0.4; // wall-follow: free a forward-only enemy pinned on a wedge diagonal
        if (moveScale > 0) noteBlock(e, dt, pushX, pushZ, moved * moved, eGoal * eGoal, tx, tz); else e.blockT -= dt;

        // mines
        if (e.type!.mines) {
          e.mineTimer -= dt;
          if (e.mineTimer <= 0 && bullets.countMines(e) < 1 && moved > 0.1) {
            bullets.layMine(e);
            e.mineTimer = 4 + Math.random() * 4;
            this.minesLaidThisLayer++;
          }
        }
      }

      // --- kamikaze: a turretless mobile mine — detonate on contact, else keep charging ---
      if (e.type!.kamikaze) {
        if (e.level === tgt.level && distP < 24) {
          bullets.layMine(e, true); // instant-armed mine — its blast hits the player next bullets.update
          e.alive = false;
        } else {
          // displayed floor 30+ (idx 29+): the kamikaze drops a TRAIL of mines as it sprints
          if (this.layer >= 29) {
            e.mineTimer -= dt;
            if (e.mineTimer <= 0) { bullets.layMine(e); e.mineTimer = 0.55; this.minesLaidThisLayer++; }
          }
          const tdx = e.pos.x - e.treadX, tdz = e.pos.z - e.treadZ; // leave a tread trail as it sprints
          if (tdx * tdx + tdz * tdz >= TREAD_STEP2) { this.treads.drop(e.pos.x, e.pos.z, e.floorY, e.bodyYaw); e.treadX = e.pos.x; e.treadZ = e.pos.z; }
          e.sync();
        }
        continue; // no turret / aiming / firing
      }

      // --- support tanks (spawner / shield generator / thrower): act, then skip aiming ---
      if (e.type!.spawner || e.type!.shieldGen || e.type!.thrower) {
        if (e.type!.spawner) this.spawnerTick(e, dt, tgt);
        else if (e.type!.shieldGen) this.shieldGenTick(e);
        else this.throwerTick(e, dt, tgt);
        const tdx = e.pos.x - e.treadX, tdz = e.pos.z - e.treadZ;
        if (tdx * tdx + tdz * tdz >= TREAD_STEP2) { this.treads.drop(e.pos.x, e.pos.z, e.floorY, e.bodyYaw); e.treadX = e.pos.x; e.treadZ = e.pos.z; }
        e.sync();
        continue; // no turret / aiming / firing
      }

      // --- LASER: stationary emitter on a CHARGE → BEAM cycle. It AIMS bounce shots
      //     (reusing the sniper's bank solver) so its beam reaches the player around
      //     corners. During the 3s charge the beam + hum fade in and the beam shifts
      //     red→pink, locking its aim just before the 3s live (pink, burning) beam. ---
      if (e.type!.laserBeam) {
        e.laserStun -= dt;
        e.laserPhaseTimer -= dt;
        if (e.laserPhaseTimer <= 0) { // flip phase
          if (e.laserPhase === "beam") { e.laserPhase = "charge"; e.laserPhaseTimer = LASER_CHARGE; e.aimScan = -1; e.aimValid = false; e.aimTimer = 0; }
          else { e.laserPhase = "beam"; e.laserPhaseTimer = LASER_BEAM; e.laserDmgT = 0.25; } // small grace so the burn doesn't land on the very first live frame
        }
        const charging = e.laserPhase === "charge";
        const sameLevel = e.level === tgt.level;
        // AIM solve — only while charging and before the lock window (the aim then freezes,
        // giving the player a beat to read the committed line). LOS → straight; else a
        // ≤2-bounce bank like the sniper, cached in aim* and reused for the live beam.
        if (charging && e.laserPhaseTimer > LASER_LOCK) {
          const losClear = sameLevel && !segmentBlocked(e.pos.x, e.pos.z, tgt.pos.x, tgt.pos.z, e.level);
          if (losClear) {
            e.aimValid = true; e.aimDirX = dxp / (distP || 1); e.aimDirZ = dzp / (distP || 1); e.aimScan = -1;
          } else if (sameLevel && e.level === 0) {
            e.aimTimer -= dt;
            if (e.aimScan < 0 && e.aimTimer <= 0) e.aimScan = 0; // begin a fresh bounce scan
            while (e.aimScan >= 0 && e.aimScan < BANK_IMAGES.length && bankBudget > 0) {
              bankBudget--;
              const [ii, jj] = BANK_IMAGES[e.aimScan++];
              const ix = imageCoord(ii, tgt.pos.x, INTERIOR.minX, INTERIOR.maxX);
              const iz = imageCoord(jj, tgt.pos.z, INTERIOR.minZ, INTERIOR.maxZ);
              let bx = ix - e.pos.x, bz = iz - e.pos.z; const bl = Math.hypot(bx, bz);
              if (bl < 1e-3) continue; bx /= bl; bz /= bl;
              if (validateBankDir(e.pos.x, e.pos.z, bx, bz, tgt.pos.x, tgt.pos.z, 1000, LASER_BOUNCES, LASER_RANGE)) {
                e.aimValid = true; e.aimDirX = bx; e.aimDirZ = bz; e.aimScan = -1; e.aimTimer = 0.3 + Math.random() * 0.2; break;
              }
            }
            if (e.aimScan >= BANK_IMAGES.length) { e.aimScan = -1; e.aimTimer = 0.4; } // none yet — keep the last aim, retry soon
          }
          const want = e.aimValid ? yawToward(e.aimDirX, e.aimDirZ) : yawToward(dxp, dzp);
          e.turretYaw = approachAngle(e.turretYaw, want, 3.0 * dt); // slew onto the solution during the charge
        }
        // draw + (in the live phase) burn along the CURRENT turret aim
        const mz = e.muzzle(this._muzzle);
        const bn = this.traceLaser(mz.x, mz.z, -Math.sin(e.turretYaw), -Math.cos(e.turretYaw), e.level, LASER_BOUNCES);
        const dHum = (e.pos.x - human.pos.x) ** 2 + (e.pos.z - human.pos.z) ** 2; // nearest laser TO THE PLAYER drives the hum
        if (charging) {
          const f = 1 - Math.max(0, e.laserPhaseTimer) / LASER_CHARGE; // 0→1 over the charge
          this.lasers.drawPath(this._laserPts, bn, mz.y, f, lerpHex(LASER_RED, LASER_PINK, f), lerpHex(LASER_RED, LASER_CORE, f)); // fade in, red→pink
          this.laserMuzzleFx(e, mz.x, mz.y, mz.z, lerpHex(LASER_RED, LASER_PINK, f), dt);
          if (dHum < humDist) { humDist = dHum; humPos.x = e.pos.x; humPos.y = e.pos.y; humPos.z = e.pos.z; humGain = f; } // hum fades in with the charge
          humOn = true;
        } else if (e.laserStun <= 0) {
          this.laserMuzzleFx(e, mz.x, mz.y, mz.z, LASER_PINK, dt);
          this.lasers.drawPath(this._laserPts, bn, mz.y); // full pink, full opacity
          this.beams.push({ pts: this._laserPts.slice(0, bn * 2), level: e.level }); // mercs read this to step off the beam
          if (dHum < humDist) { humDist = dHum; humPos.x = e.pos.x; humPos.y = e.pos.y; humPos.z = e.pos.z; humGain = 1; }
          humOn = true;
          // a PLAYER shell crossing the live beam staggers it for a moment
          for (const s of bullets.shells) {
            if (s.dead || !s.byPlayer || s.arc || s.level !== e.level) continue;
            if (this.nearLaser(s.pos.x, s.pos.z, bn) < BULLET.radius + 7) { e.laserStun = 0.25; break; }
          }
          // burn opposite-team tanks (player + mercs) standing in the beam, on a tick
          e.laserDmgT -= dt;
          if (e.laserDmgT <= 0) {
            let burned = false;
            const victims = this.allies.includes(human) ? [...this.allies] : [human, ...this.allies]; // de-dupe: in the demo `human` IS allies[0]
            for (const t of victims) {
              if (!t.alive || t.riding || t.thrown || t.riseTime > 0 || t.level !== e.level) continue;
              if (t.invulnT > 0) continue; // an Aegis shield blocks the beam too
              if (t.isMecha && !t.crouched) continue; // a striding walker shrugs off the beam (legs only via direct shots)
              if (this.nearLaser(t.pos.x, t.pos.z, bn) > TANK.radius + 5) continue;
              burned = true;
              if (t.isPlayer) this.laserHitPlayer = true; // deferred to main (grace + lives)
              else { t.health -= 1; if (t.health <= 0) { t.alive = false; t.killer = e; } } // a merc — chip its shields / kill
            }
            if (burned) e.laserDmgT = 0.5;
          }
        }
        const tdx = e.pos.x - e.treadX, tdz = e.pos.z - e.treadZ;
        if (tdx * tdx + tdz * tdz >= TREAD_STEP2) { this.treads.drop(e.pos.x, e.pos.z, e.floorY, e.bodyYaw); e.treadX = e.pos.x; e.treadZ = e.pos.z; }
        e.sync();
        continue;
      }

      // --- MLRS: a STATIONARY launcher that lobs rapid, wildly inaccurate SALVOS of a
      //     dozen arc shells at infinite range (the arc clears walls; no LOS needed). ---
      if (e.type!.mlrs) {
        e.turretYaw = approachAngle(e.turretYaw, yawToward(dxp, dzp), 2.5 * dt); // face the target (cosmetic)
        if (e.burstLeft > 0) {
          e.fireTimer -= dt;
          if (e.fireTimer <= 0) {
            const SC = 220; // wildly inaccurate scatter around the target
            bullets.fireArc(e, tgt.pos.x + (Math.random() * 2 - 1) * SC, tgt.pos.z + (Math.random() * 2 - 1) * SC, tgt.level, tgt.floorY);
            if (e.burstLeft % 4 === 0) sfx.play("artillery", e.pos); // a few salvo thunks, not 12 (avoid an audio wall)
            e.burstLeft--;
            e.fireTimer = 0.09; // rapid within the salvo
          }
        } else {
          e.mineTimer -= dt; // between-salvo cooldown (reuses the mine timer)
          if (e.mineTimer <= 0) { e.burstLeft = 12; e.fireTimer = 0; e.mineTimer = 4 + Math.random() * 2; }
        }
        e.sync();
        continue; // never uses the standard aim/fire
      }

      // --- aim: direct if there's a clear line, else bank a shell off the walls ---
      // shells are level-locked, so only a same-level player can be hit.
      const sameLevel = e.level === tgt.level;
      const losClear = sameLevel && !segmentBlocked(e.pos.x, e.pos.z, tgt.pos.x, tgt.pos.z, e.level);
      let want: number;
      let canShoot: boolean;
      let precise = false; // a solved multi-bounce shot — fire EXACTLY, no aim jitter
      if (e.type!.artillery) {
        // lobs arc over walls — no LOS needed; just face the player and stay in lob range
        want = yawToward(dxp, dzp);
        canShoot = distP < 660;
      } else if (e.type!.tow) {
        // homing missiles steer themselves — face the player; the missile finds the way
        want = yawToward(dxp, dzp);
        canShoot = sameLevel;
      } else if (losClear) {
        // lead the player's motion for a direct shot
        const travel = distP / e.type!.bulletSpeed;
        const aimX = tgt.pos.x + tgtVel.x * e.type!.lead * travel;
        const aimZ = tgt.pos.z + tgtVel.z * e.type!.lead * travel;
        want = yawToward(aimX - e.pos.x, aimZ - e.pos.z);
        canShoot = true;
      } else if (sameLevel && e.level === 0 && e.type!.aimBounces) {
        // god-like aim: a perfect multi-bounce bank shot, searched INCREMENTALLY
        // (a few candidates per frame, shared global budget) so it never spikes.
        // The previous solution keeps firing while a fresh scan runs.
        e.aimTimer -= dt;
        if (e.aimScan < 0 && e.aimTimer <= 0) e.aimScan = 0; // begin a fresh scan
        while (e.aimScan >= 0 && e.aimScan < BANK_IMAGES.length && bankBudget > 0) {
          bankBudget--;
          const [ii, jj] = BANK_IMAGES[e.aimScan++];
          const ix = imageCoord(ii, tgt.pos.x, INTERIOR.minX, INTERIOR.maxX);
          const iz = imageCoord(jj, tgt.pos.z, INTERIOR.minZ, INTERIOR.maxZ);
          let dx = ix - e.pos.x, dz = iz - e.pos.z;
          const len = Math.hypot(dx, dz);
          if (len < 1e-3) continue;
          dx /= len; dz /= len;
          // solve the bank from the MUZZLE (where the shell actually starts), NOT the
          // tank centre — the muzzle is ~a barrel-length forward, and solving from the
          // centre shifted the whole bounce path so pentabounce shots consistently missed.
          // The muzzle sits on the centre→image ray, so the direction is unchanged.
          const md = -(e.muzzleLocals[0]?.z ?? -15); // muzzle forward distance
          if (validateBankDir(e.pos.x + dx * md, e.pos.z + dz * md, dx, dz, tgt.pos.x, tgt.pos.z, e.type!.bulletSpeed)) {
            e.aimValid = true; e.aimDirX = dx; e.aimDirZ = dz; e.aimScan = -1; e.aimTimer = 0.5 + Math.random() * 0.3;
            break;
          }
        }
        if (e.aimScan >= BANK_IMAGES.length) { e.aimValid = false; e.aimScan = -1; e.aimTimer = 1.2 + Math.random() * 0.7; } // none found
        if (e.aimValid) { want = yawToward(e.aimDirX, e.aimDirZ); canShoot = true; precise = true; }
        else { want = yawToward(tgt.pos.x - e.pos.x, tgt.pos.z - e.pos.z); canShoot = false; }
      } else if (sameLevel && e.level === 0 && e.type!.bounces >= 1) {
        // no line of sight — bank a shell off a wall (ground only; no lead). Solving
        // scans every maze wall, so THROTTLE it (cache the result, re-solve a few
        // times/sec) and share the per-frame bankBudget with snipers — otherwise
        // this ran every frame for every bouncer and blew up on big floors. A
        // non-sniper bouncer never uses the aim* fields, so we reuse them here.
        e.aimTimer -= dt;
        if (e.aimTimer <= 0 && bankBudget > 0) {
          bankBudget--;
          const bank = bankShotDir(e.pos.x, e.pos.z, tgt.pos.x, tgt.pos.z);
          e.aimValid = !!bank;
          if (bank) { e.aimDirX = bank.x; e.aimDirZ = bank.z; }
          e.aimTimer = 0.35 + Math.random() * 0.25;
        }
        if (e.aimValid) { want = yawToward(e.aimDirX, e.aimDirZ); canShoot = true; }
        else { want = yawToward(tgt.pos.x - e.pos.x, tgt.pos.z - e.pos.z); canShoot = false; } // track but hold fire
      } else {
        want = yawToward(tgt.pos.x - e.pos.x, tgt.pos.z - e.pos.z);
        canShoot = false;
      }
      e.turretYaw = approachAngle(e.turretYaw, want, 4.0 * dt);

      // --- firing --- (shells are level-locked, so only fire at a same-level player)
      e.fireTimer -= dt;
      // precise bank shots need a tight alignment gate + fire EXACTLY (no jitter)
      const aimGate = precise ? 0.04 : 0.16;
      const aimed = Math.abs(((e.turretYaw - want + Math.PI * 3) % (Math.PI * 2)) - Math.PI) < aimGate;
      // a precise bank is cached for up to ~0.8s; a MOBILE sniper may have drifted or the
      // target moved since. The instant before committing, re-solve from the current muzzle
      // and DROP the shot if it no longer connects — a mobile bank sniper that fires a stale
      // solution just misses. (A static Sniper always re-confirms, so it's unaffected.)
      let preciseOK = true;
      if (precise && e.fireTimer <= 0 && aimed) {
        const fx = -Math.sin(want), fz = -Math.cos(want), md = -(e.muzzleLocals[0]?.z ?? -15);
        preciseOK = validateBankDir(e.pos.x + fx * md, e.pos.z + fz * md, fx, fz, tgt.pos.x, tgt.pos.z, e.type!.bulletSpeed);
        if (!preciseOK) { e.aimValid = false; e.aimScan = -1; e.aimTimer = 0; } // stale — re-acquire at once instead of wasting the shot
      }
      if (e.fireTimer <= 0 && aimed && canShoot && preciseOK && (e.type!.artillery || sameLevel) && bullets.countShells(e) < e.type!.maxShells + euShells) {
        const eSpd = e.type!.bulletSpeed * euShell, eBnc = Math.max(0, e.type!.bounces + euBounce); // enemy-team perks
        if (e.type!.artillery) {
          // lob to where the player is heading (the round is telegraphed + dodgeable)
          bullets.fireArc(e, tgt.pos.x + tgtVel.x * e.type!.lead, tgt.pos.z + tgtVel.z * e.type!.lead, tgt.level, tgt.floorY);
        } else if (e.type!.tow) {
          bullets.fireHoming(e, tgt);
        } else if (precise) {
          e.turretYaw = want; // snap to the solved direction; no aim error
          bullets.fire(e, eSpd, eBnc, false, SNIPER_LIFE, 1, true); // precise = fixed-step, traces the solver's path exactly
        } else {
          e.turretYaw += (Math.random() - 0.5) * 2 * e.type!.aimError; // bake aim error in at fire time
          // a sniper firing a direct shot still gets the long shell life (consistency)
          bullets.fire(e, eSpd, eBnc, !!e.type!.rocket, e.type!.aimBounces ? SNIPER_LIFE : BULLET.life);
        }
        if (e.type!.artillery) sfx.play("artillery", e.pos); // distinct mortar thunk (positional)
        else sfx.play("fire"); // enemies use the same shot sound as the player
        e.turretYaw = want;
        e.fireTimer = e.type!.fireInterval * this.fireScale() * euCool * (0.8 + Math.random() * 0.5);
        // a MOBILE bank sniper (Stalker) drops its solution after firing and re-acquires
        // ~0.7s before the next shot — so it kites/repositions through the cooldown but the
        // shot it takes is always solved from where it is standing, not a stale spot.
        if (precise && e.type!.move === "kite") { e.aimValid = false; e.aimScan = -1; e.aimTimer = Math.max(0.2, e.fireTimer - 0.7); }
      }

      // gatling: spin the barrel cluster up while it has a shot (idle-coasts otherwise)
      e.updateSpin(dt, canShoot && sameLevel);

      // --- ABOMINATION: on its own cooldown, unleash one random extra ability ---
      if (e.type!.abomination) {
        e.thinkTimer -= dt;
        if (e.thinkTimer <= 0) { this.abominationAbility(e, tgt, bullets); e.thinkTimer = 1.6 + Math.random() * 1.4; }
      }

      // laser sight: trace the SOLVED firing solution (`want`) from the solved muzzle —
      // NOT the slewing barrel. These snipers only ever shoot a validated bank straight at
      // tgt.pos (no lead), so the trace ricochets exactly like the shell and STOPS on the
      // player. Following the live turretYaw instead made the sight flail across the arena
      // (and re-trace the full RANGE every frame) while the barrel caught up to the solution.
      if (e.type!.laser && canShoot && sameLevel) {
        const mz0 = e.muzzleLocals[0];
        const md = -(mz0?.z ?? -15), my = e.floorY + (mz0?.y ?? 6.875); // muzzle forward dist + height (snipers are single-barrel, no lateral offset)
        const fx = -Math.sin(want), fz = -Math.cos(want);
        this.lasers.add(e.pos.x + fx * md, my, e.pos.z + fz * md, fx, fz, e.level, e.type!.bounces, tgt.pos.x, tgt.pos.z);
      }

      e.sync();

      // leave tread marks as the tank drives (distance-gated; one draw call total)
      if (e.riseTime <= 0 && !e.riding) {
        const tdx = e.pos.x - e.treadX, tdz = e.pos.z - e.treadZ;
        if (tdx * tdx + tdz * tdz >= TREAD_STEP2) {
          this.treads.drop(e.pos.x, e.pos.z, e.floorY, e.bodyYaw);
          e.treadX = e.pos.x; e.treadZ = e.pos.z;
        }
      }
    }
    this.lasers.end(); // hide laser beams whose sniper didn't draw one this frame
    sfx.laserHum(humOn, humOn ? humPos : undefined, humGain); // sustained wobbling sine while any laser fires (gain ramps up over a charge)

    // drop destroyed enemies (bullets set alive=false)
    for (const e of this.enemies) {
      if (!e.alive) { this.onTankKilled(e, e.killer); e.removeFromScene(this.scene); e.dispose(); }
    }
    this.enemies = this.enemies.filter((e) => e.alive);
  }
}
